import React, { useState, useRef, useEffect } from 'react'
import { useAuth } from '../hooks/useAuth'
import { useImpersonation } from '../hooks/useImpersonation'
import api, { API_BASE_URL } from '../lib/api'
import { formatPhoneDisplay } from '@/lib/utils'
import { User, Lock, Code, Save, Upload, Bell, Phone, PhoneCall, Volume2, ExternalLink, Zap, Users2, AlertTriangle, Building2, Bot, Loader2, Trash2, Mail, Briefcase, Globe2, Shield, ChevronRight, Sparkles, Calendar } from 'lucide-react'
import { Link, useNavigate } from 'react-router-dom'
// AgentBuilder wizard moved to AI Agents page
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Button } from '@/components/ui/button'
import { Textarea } from '@/components/ui/textarea'
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar'
import { useToast } from '../hooks/use-toast'
import { Badge } from '@/components/ui/badge'
import { Separator } from '@/components/ui/separator'
import { Switch } from '@/components/ui/switch'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip'
import { US_TIMEZONES, detectBrowserTimezone } from '@/lib/timezones'
import { useFeatureGate, Feature, Tier } from '@/hooks/useFeatureGate'
import { useBrand, splitBrandName } from '@/hooks/useBrand'
import { TierBadge, STANDARD_TIER_MONTHLY_CENTS, formatMonthly } from '@/components/ui/TierBadge'
import MobileNavSettings from '@/components/settings/MobileNavSettings'

interface AlertChannel {
  checked: boolean
  onChange: (checked: boolean) => void
  disabled?: boolean
  comingSoon?: boolean
}

/**
 * Upsell panel shown in place of a tab's real content when the current org's
 * tier doesn't include the feature. Keeps the tab clickable (soft gating) so
 * users can preview what they'd get and upgrade without leaving the page.
 */
function LockedSettingsPanel({
  feature,
  tier,
  title,
  description,
}: {
  feature: Feature
  tier: Tier
  title: string
  description: string
}) {
  const navigate = useNavigate()
  const priceCents = STANDARD_TIER_MONTHLY_CENTS[tier] ?? 0
  return (
    <Card className="border bg-card shadow-sm card-inset overflow-hidden">
      <CardContent className="py-10 px-6 sm:px-10">
        <div className="flex items-start gap-4">
          <div className="shrink-0 h-12 w-12 rounded-xl bg-gradient-to-br from-brand-royal/10 to-brand-periwinkle/10 border border-brand-royal/20 flex items-center justify-center">
            <Lock className="h-6 w-6 text-brand-royal" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-1">
              <p className="text-[11px] uppercase tracking-wider text-muted-foreground">Locked</p>
              <TierBadge tier={tier} size="sm" />
              <span className="text-xs text-muted-foreground">{formatMonthly(priceCents)}/mo</span>
            </div>
            <h2 className="text-xl sm:text-2xl font-heading font-semibold tracking-tight">{title}</h2>
            <p className="text-sm text-muted-foreground mt-2 leading-relaxed">{description}</p>
            <div className="flex flex-wrap gap-2 mt-5">
              <Button
                onClick={() => navigate('/settings/billing')}
                className="bg-gradient-to-r from-brand-royal to-brand-periwinkle hover:brightness-110 text-white"
              >
                Upgrade to <span className="capitalize ml-1">{tier}</span>
              </Button>
            </div>
            <p className="text-[10px] text-muted-foreground/60 mt-3">Feature: {feature}</p>
          </div>
        </div>
      </CardContent>
    </Card>
  )
}

function AlertRow({
  title,
  desc,
  sound,
  desktop,
  email,
  tierGate,
}: {
  title: string
  desc: string
  sound?: AlertChannel
  desktop?: AlertChannel
  email?: AlertChannel
  /** When set, render the row muted + disabled and show a tooltip saying
      which tier unlocks it. Keeps the row visible so users can see what
      they'd get, without letting them toggle switches that would never fire. */
  tierGate?: { locked: boolean; tier: 'partner' | 'principal' }
}) {
  const isGated = tierGate?.locked === true

  const renderCell = (channel: AlertChannel | undefined, label: string, width: string) => {
    if (!channel) {
      return <div className={`${width} hidden sm:block`} aria-hidden />
    }
    return (
      <div className={`flex flex-1 flex-col items-center gap-1.5 sm:flex-initial sm:justify-center ${width}`}>
        <span className="text-[11px] text-muted-foreground sm:hidden">{label}</span>
        <div className="flex flex-col items-center gap-0.5">
          <Switch
            checked={channel.checked}
            onCheckedChange={channel.onChange}
            disabled={isGated || channel.disabled || channel.comingSoon}
          />
          {channel.comingSoon && (
            <span className="text-[10px] text-muted-foreground leading-tight">Coming soon</span>
          )}
        </div>
      </div>
    )
  }

  const rowContent = (
    <div className={`py-4 flex flex-col sm:grid sm:grid-cols-[1fr,auto,auto,auto] sm:items-center sm:gap-4 ${isGated ? 'opacity-50' : ''}`}>
      <div className="space-y-0.5 mb-3 sm:mb-0">
        <div className="text-sm font-medium text-foreground flex items-center gap-2">
          {title}
          {isGated && (
            <span className="text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded-full bg-muted text-muted-foreground border capitalize">
              {tierGate!.tier}+
            </span>
          )}
        </div>
        <p className="text-xs text-muted-foreground">{desc}</p>
      </div>
      <div className="flex gap-4 sm:contents">
        {renderCell(sound, 'Sound', 'w-16')}
        {renderCell(desktop, 'Desktop', 'w-16')}
        {renderCell(email, 'Email', 'w-20')}
      </div>
    </div>
  )

  if (!isGated) return rowContent

  return (
    <TooltipProvider delayDuration={100}>
      <Tooltip>
        <TooltipTrigger asChild>
          <div>{rowContent}</div>
        </TooltipTrigger>
        <TooltipContent>
          <p className="text-xs">
            This alert fires on <span className="font-semibold capitalize">{tierGate!.tier}</span> features your current plan doesn't include.
          </p>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  )
}

export default function SettingsPage() {
  const { agent, updateProfile, refreshAgent } = useAuth()
  const brand = useBrand()
  const brandBaseName = splitBrandName(brand.appName)[0]
  const featureGate = useFeatureGate()
  // Tier-gating for Settings tabs: Voice requires Partner (ai_inbound_agent),
  // Lead Follow-Up requires Principal (ai_outbound_agent). Locked tabs show
  // a lock icon + tooltip, and the tab content renders an upsell panel
  // instead of the usual controls if someone lands on them.
  const voiceLocked = !featureGate.isAllowed('ai_inbound_agent')
  const followUpLocked = !featureGate.isAllowed('ai_outbound_agent')
  const { isImpersonating } = useImpersonation()
  const { toast } = useToast()
  const navigate = useNavigate()
  const [loading, setLoading] = useState(false)
  const fileInputRef = useRef<HTMLInputElement>(null)
  const [uploadingAvatar, setUploadingAvatar] = useState(false)
  // Profile form state — syncs with auth agent data when it changes
  const [profileData, setProfileData] = useState({
    name: agent?.name || '',
    email: agent?.email || '',
    department: agent?.department || '',
    avatarUrl: agent?.avatarUrl || '',
    timezone: (agent as any)?.timezone || detectBrowserTimezone(),
  })

  // When impersonating, load the impersonated org's admin profile instead of your own
  useEffect(() => {
    if (isImpersonating) {
      api.get('/api/organizations/current').then(res => {
        if (res.data.success && res.data.organization) {
          // Load org members to find the admin
          const orgId = res.data.organization.id
          api.get(`/api/organizations/${orgId}/members`).then(membersRes => {
            if (membersRes.data.success && membersRes.data.members?.length > 0) {
              const admin = membersRes.data.members.find((m: any) => m.role === 'admin') || membersRes.data.members[0]
              setProfileData(prev => ({
                ...prev,
                name: admin.name || '',
                email: admin.email || '',
                department: admin.department || '',
                avatarUrl: admin.avatar || '',
                timezone: admin.timezone || prev.timezone,
              }))
            }
          }).catch(() => {})
        }
      }).catch(() => {})
    }
  }, [isImpersonating])

  // Keep profileData in sync when agent data refreshes (e.g., after token verify on page load)
  useEffect(() => {
    if (agent && !isImpersonating) {
      setProfileData(prev => ({
        name: prev.name || agent.name || '',
        email: prev.email || agent.email || '',
        department: prev.department || agent.department || '',
        avatarUrl: agent.avatarUrl || prev.avatarUrl || '',
        timezone: prev.timezone || (agent as any).timezone || detectBrowserTimezone(),
      }))
    }
  }, [agent, isImpersonating])

  // Notification preferences — per-event toggles for Sound / Desktop / Email.
  // Email cells that aren't wired yet are shown with a "Coming soon" badge in the UI.
  const [notificationSettings, setNotificationSettings] = useState({
    // Master switches
    soundEnabled: true,
    desktopNotifications: true,

    // Per-event sound toggles
    newMessageSound: true,
    chatRequestSound: true,
    teamMessageSound: true,
    humanEscalationSound: true,
    bookingCreatedSound: true,
    bookingReminderSound: true,
    callbackExhaustedSound: true,
    affiliateEmailSound: true,

    // Per-event desktop toggles
    newMessageDesktop: true,
    chatRequestDesktop: true,
    teamMessageDesktop: true,
    humanEscalationDesktop: true,
    bookingCreatedDesktop: true,
    bookingReminderDesktop: true,
    callbackExhaustedDesktop: true,
    affiliateEmailDesktop: true,

    // Per-event email toggles (most unwired — see EMAIL-WIRING-SPEC.md)
    humanEscalationEmail: false,
    bookingCreatedEmail: false,
    bookingReminderEmail: false,
    leadSummaryEmail: true, // admin opt-out for post-call summary (currently the only wired email)
  })

  // Password form state
  const [passwordData, setPasswordData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  })

  // Delete account state
  const [deleteStep, setDeleteStep] = useState(0) // 0=hidden, 1=password, 2=confirm name, 3=final
  const [deletePassword, setDeletePassword] = useState('')
  const [deleteOrgName, setDeleteOrgName] = useState('')
  const [deleteLoading, setDeleteLoading] = useState(false)
  const [deleteError, setDeleteError] = useState('')

  // Widget + integration settings previously lived here — rendering moved to
  // WidgetPage and FormConnectionsPage. State blobs were loaded but never shown.

  // Voice settings state
  const [voiceSettings, setVoiceSettings] = useState({
    assistantName: '',
    greeting: ''
  })
  const [phoneNumbers, setPhoneNumbers] = useState<any[]>([])
  const [assistants, setAssistants] = useState<any[]>([])
  const [loadingVoice, setLoadingVoice] = useState(false)
  const [savingVoice, setSavingVoice] = useState(false)
  const [savingCalendarConfig, setSavingCalendarConfig] = useState(false)

  // Voice preview state
  const [playingVoice, setPlayingVoice] = useState<string | null>(null)
  const voiceAudioRef = useRef<HTMLAudioElement | null>(null)

  const handlePreviewVoice = async (voiceId: string) => {
    // Stop if already playing this voice
    if (playingVoice === voiceId) {
      voiceAudioRef.current?.pause()
      setPlayingVoice(null)
      return
    }
    // Stop any currently playing
    voiceAudioRef.current?.pause()
    setPlayingVoice(voiceId)
    try {
      const res = await api.get(`/api/voice/preview/${voiceId}`, { responseType: 'blob' })
      const url = URL.createObjectURL(res.data)
      const audio = new Audio(url)
      voiceAudioRef.current = audio
      audio.onended = () => setPlayingVoice(null)
      audio.onerror = () => setPlayingVoice(null)
      await audio.play()
    } catch {
      toast({ title: 'Preview unavailable', description: 'Could not load voice preview.', variant: 'destructive' })
      setPlayingVoice(null)
    }
  }

  // Concurrency / call capacity state
  const [concurrencyStatus, setConcurrencyStatus] = useState({
    activeCount: 0,
    effectiveLimit: 10,
    utilization: 0,
    queueDepth: 0,
  })

  // Callback config state
  const [callbackConfig, setCallbackConfig] = useState({
    enabled: false,
    maxRetries: 5,
    expiryHours: 48,
    delayMinutes: 5,
    autoCancelOnCallback: true,
    businessHoursOnly: true,
    retryCadence: [5, 30, 120, 480] as number[],
    // Per-recipient cooldown — refuse a fresh outbound to the same
    // number within this window, regardless of trigger source. 10min
    // is a safe default; 0 disables the guardrail.
    recipientCooldownMinutes: 10,
  })
  const [savingCallback, setSavingCallback] = useState(false)

  // Booking reminder config state
  const [bookingReminderConfig, setBookingReminderConfig] = useState({
    attorneyReminderEnabled: true,
    attorneyReminderMinutes: 15,
    reminderSoundEnabled: true,
    clientConfirmationEnabled: false,
    confirmationHoursBefore: 24,
    confirmationMinDaysOut: 2,
    // Outbound prospect emails (calendar invites + reschedule notifications).
    // Off by default — orgs only get these if explicitly opted in. Lives at
    // settings.bookingInvitesEnabled (top-level), not under bookingReminderConfig.
    bookingInvitesEnabled: false,
  })
  const [savingBookingReminder, setSavingBookingReminder] = useState(false)

  // Recording disclosure config — TCPA / two-party-consent compliance.
  // Outbound default is on (when feature enabled); inbound is opt-in.
  const [disclosureConfig, setDisclosureConfig] = useState({
    enabled: false,
    message: 'Just a quick heads up — this call may be recorded for quality.',
    applyToInbound: false,
    applyToOutbound: true,
  })
  const [savingDisclosure, setSavingDisclosure] = useState(false)

  // Voicemail TTS preview — lets admins hear what their voicemail will sound
  // like in the chosen voice before a real outbound call hits a voicemail box.
  const [previewingVoicemail, setPreviewingVoicemail] = useState(false)
  const voicemailAudioRef = useRef<HTMLAudioElement | null>(null)

  // Per-org Do-Not-Call list. STOP-keyword replies populate this automatically;
  // admins can also add/remove entries manually.
  type DNCEntry = {
    id: string
    phoneNumber: string
    source: 'manual' | 'reply' | 'callee_request'
    notes: string | null
    createdAt: string
  }
  const [dncEntries, setDncEntries] = useState<DNCEntry[]>([])
  const [dncTotal, setDncTotal] = useState(0)
  const [dncLoading, setDncLoading] = useState(false)
  const [dncSearch, setDncSearch] = useState('')
  const [dncNewNumber, setDncNewNumber] = useState('')
  const [dncNewNotes, setDncNewNotes] = useState('')
  const [dncAdding, setDncAdding] = useState(false)

  // Voice provider credentials (internal — no user-facing UI)

  // Guided customization state (wizard now lives on AI Agents page)
  const [voiceConfig, setVoiceConfig] = useState({
    voiceId: 'lauren' as string,
    tonePreset: 'balanced' as 'warm' | 'balanced' | 'formal',
    greetingName: '',
    callbackTimeframe: '24 hours',
    practiceAreas: [] as string[],
    wizardCompleted: false,
    wizardResponses: {} as any,
    firmTimezone: detectBrowserTimezone(),
    businessHours: 'Monday through Friday, 9am to 5pm',
    bookingConfig: {
      consultationLengthMinutes: 30,
      businessHoursOnly: true,
      blackoutDates: [] as string[],
    },
  })

  // Organization settings state
  const [orgData, setOrgData] = useState({
    id: '',
    name: '',
    slug: '',
    subscriptionTier: '',
  })
  const [orgOriginal, setOrgOriginal] = useState({ name: '', slug: '' })
  const [loadingOrg, setLoadingOrg] = useState(false)
  const [savingOrg, setSavingOrg] = useState(false)

  const [savingNotifications, setSavingNotifications] = useState(false)
  const [isAffiliate, setIsAffiliate] = useState(false)

  // Detect affiliate status so we only show the affiliate-email alert row to
  // users who actually have a partner inbox. Same /api/affiliates/me probe
  // the sidebar uses.
  useEffect(() => {
    let cancelled = false
    api.get('/api/affiliates/me')
      .then(() => { if (!cancelled) setIsAffiliate(true) })
      .catch(() => { if (!cancelled) setIsAffiliate(false) })
    return () => { cancelled = true }
  }, [])

  // Load notification preferences on mount
  useEffect(() => {
    api.get('/api/auth/preferences').then(res => {
      if (res.data.success && res.data.preferences) {
        const p = res.data.preferences
        // humanEscalationAlert is the legacy name — dual-read for backwards compat
        const escalationSound = p.humanEscalationSound ?? p.humanEscalationAlert
        setNotificationSettings(prev => ({
          soundEnabled: p.soundEnabled ?? prev.soundEnabled,
          desktopNotifications: p.desktopNotifications ?? prev.desktopNotifications,

          newMessageSound: p.newMessageSound ?? prev.newMessageSound,
          chatRequestSound: p.chatRequestSound ?? prev.chatRequestSound,
          teamMessageSound: p.teamMessageSound ?? prev.teamMessageSound,
          humanEscalationSound: escalationSound ?? prev.humanEscalationSound,
          bookingCreatedSound: p.bookingCreatedSound ?? prev.bookingCreatedSound,
          bookingReminderSound: p.bookingReminderSound ?? prev.bookingReminderSound,
          callbackExhaustedSound: p.callbackExhaustedSound ?? prev.callbackExhaustedSound,
          affiliateEmailSound: p.affiliateEmailSound ?? prev.affiliateEmailSound,

          newMessageDesktop: p.newMessageDesktop ?? prev.newMessageDesktop,
          chatRequestDesktop: p.chatRequestDesktop ?? prev.chatRequestDesktop,
          teamMessageDesktop: p.teamMessageDesktop ?? prev.teamMessageDesktop,
          humanEscalationDesktop: p.humanEscalationDesktop ?? prev.humanEscalationDesktop,
          bookingCreatedDesktop: p.bookingCreatedDesktop ?? prev.bookingCreatedDesktop,
          bookingReminderDesktop: p.bookingReminderDesktop ?? prev.bookingReminderDesktop,
          callbackExhaustedDesktop: p.callbackExhaustedDesktop ?? prev.callbackExhaustedDesktop,
          affiliateEmailDesktop: p.affiliateEmailDesktop ?? prev.affiliateEmailDesktop,

          humanEscalationEmail: p.humanEscalationEmail ?? prev.humanEscalationEmail,
          bookingCreatedEmail: p.bookingCreatedEmail ?? prev.bookingCreatedEmail,
          bookingReminderEmail: p.bookingReminderEmail ?? prev.bookingReminderEmail,
          leadSummaryEmail: p.leadSummaryEmail ?? prev.leadSummaryEmail,
        }))
      }
    }).catch((err) => console.warn('Failed to load notification preferences:', err?.message))
  }, [])

  // Load organization settings on mount
  useEffect(() => {
    if ((agent?.role === 'admin' || isImpersonating)) {
      loadOrgSettings()
    }
  }, [agent])

  const loadOrgSettings = async () => {
    setLoadingOrg(true)
    try {
      const res = await api.get('/api/organizations/current')
      if (res.data.success && res.data.organization) {
        const org = res.data.organization
        setOrgData({
          id: org.id,
          name: org.name || '',
          slug: org.slug || '',
          subscriptionTier: org.subscriptionTier || org.subscription_tier || '',
        })
        setOrgOriginal({ name: org.name || '', slug: org.slug || '' })
      }
    } catch (err) {
      console.error('Failed to load organization settings:', err)
    } finally {
      setLoadingOrg(false)
    }
  }

  const handleSaveNotifications = async () => {
    setSavingNotifications(true)
    try {
      await api.put('/api/auth/preferences', { preferences: notificationSettings })
      toast({ title: 'Saved', description: 'Notification preferences updated.' })
    } catch {
      toast({ title: 'Error', description: 'Failed to save notification preferences.', variant: 'destructive' })
    } finally {
      setSavingNotifications(false)
    }
  }

  const handleSaveOrgSettings = async () => {
    if (!orgData.id) return
    if (!orgData.slug || !orgData.slug.trim()) {
      toast({
        title: 'Slug required',
        description: 'Organization URL slug cannot be empty.',
        variant: 'destructive',
      })
      return
    }
    setSavingOrg(true)
    try {
      const updates: any = {}
      if (orgData.name !== orgOriginal.name) updates.name = orgData.name
      if (orgData.slug !== orgOriginal.slug) updates.slug = orgData.slug

      const res = await api.put(`/api/organizations/${orgData.id}`, updates)
      if (res.data.success) {
        setOrgOriginal({ name: orgData.name, slug: orgData.slug })
        toast({
          title: 'Organization updated',
          description: 'Organization settings have been saved.',
        })
      }
    } catch (err: any) {
      toast({
        title: 'Error',
        description: err.response?.data?.message || 'Failed to update organization',
        variant: 'destructive',
      })
    } finally {
      setSavingOrg(false)
    }
  }

  // Load voice settings on mount (admin only)
  useEffect(() => {
    if ((agent?.role === 'admin' || isImpersonating)) {
      loadVoiceSettings()
    }
  }, [agent])

  // Load booking reminder config for all users (shown on Notifications tab)
  useEffect(() => {
    api.get('/api/voice/booking-reminder-config').then(res => {
      if (res.data?.success) {
        const rc = res.data.config
        setBookingReminderConfig({
          attorneyReminderEnabled: rc.attorneyReminderEnabled ?? true,
          attorneyReminderMinutes: rc.attorneyReminderMinutes ?? 15,
          reminderSoundEnabled: rc.reminderSoundEnabled ?? true,
          clientConfirmationEnabled: rc.clientConfirmationEnabled ?? false,
          confirmationHoursBefore: rc.confirmationHoursBefore ?? 24,
          confirmationMinDaysOut: rc.confirmationMinDaysOut ?? 2,
          bookingInvitesEnabled: rc.bookingInvitesEnabled === true,
        })
      }
    }).catch(() => {})
  }, [])

  const loadVoiceSettings = async () => {
    setLoadingVoice(true)
    try {
      const [numbersRes, assistantsRes, configRes, concStatusRes, cbConfigRes, reminderConfigRes, disclosureRes] = await Promise.all([
        api.get('/api/voice/numbers'),
        api.get('/api/voice/assistants'),
        api.get('/api/voice/config'),
        api.get('/api/voice/concurrency?scope=org').catch(() => null),
        api.get('/api/voice/callback-config').catch(() => null),
        api.get('/api/voice/booking-reminder-config').catch(() => null),
        api.get('/api/voice/disclosure-config').catch(() => null),
      ])

      if (numbersRes.data.success) {
        setPhoneNumbers(numbersRes.data.phoneNumbers || [])
      }
      if (assistantsRes.data.success) {
        const assistantList = assistantsRes.data.assistants || []
        setAssistants(assistantList)
        // If there's an assistant, populate the form
        if (assistantList.length > 0) {
          const assistant = assistantList[0]
          setVoiceSettings(prev => ({
            ...prev,
            assistantName: assistant.name || '',
            greeting: assistant.firstMessage || ''
          }))
        }
      }
      // Load voice config for guided customization
      if (configRes.data.success && configRes.data.config) {
        const loaded = configRes.data.config
        setVoiceConfig(prev => ({
          ...prev,
          ...loaded,
          firmTimezone: loaded.firmTimezone || loaded.timezone || prev.firmTimezone,
          businessHours: loaded.businessHours || prev.businessHours,
          bookingConfig: {
            ...prev.bookingConfig,
            ...(loaded.bookingConfig || {}),
            blackoutDates: Array.isArray(loaded.bookingConfig?.blackoutDates)
              ? loaded.bookingConfig.blackoutDates
              : prev.bookingConfig.blackoutDates,
          },
        }))
      }
      // Approved voices fetch removed – state was orphaned (values never read in render)
      // Load live concurrency status
      if (concStatusRes?.data?.success) {
        setConcurrencyStatus({
          activeCount: concStatusRes.data.activeCount,
          effectiveLimit: concStatusRes.data.effectiveLimit,
          utilization: concStatusRes.data.utilization,
          queueDepth: concStatusRes.data.queueDepth || 0,
        })
      }
      if (cbConfigRes?.data?.success) {
        const cb = cbConfigRes.data.config
        setCallbackConfig(prev => ({
          ...prev,
          ...cb,
          businessHoursOnly: cb.businessHoursOnly ?? true,
          retryCadence: cb.retryCadence ?? [5, 30, 120, 480],
          recipientCooldownMinutes: cb.recipientCooldownMinutes ?? 10,
        }))
      }
      if (reminderConfigRes?.data?.success) {
        const rc = reminderConfigRes.data.config
        setBookingReminderConfig({
          attorneyReminderEnabled: rc.attorneyReminderEnabled ?? true,
          attorneyReminderMinutes: rc.attorneyReminderMinutes ?? 15,
          reminderSoundEnabled: rc.reminderSoundEnabled ?? true,
          clientConfirmationEnabled: rc.clientConfirmationEnabled ?? false,
          confirmationHoursBefore: rc.confirmationHoursBefore ?? 24,
          confirmationMinDaysOut: rc.confirmationMinDaysOut ?? 2,
          bookingInvitesEnabled: rc.bookingInvitesEnabled === true,
        })
      }
      if (disclosureRes?.data?.success) {
        const dc = disclosureRes.data.config
        setDisclosureConfig({
          enabled: dc.enabled ?? false,
          message: dc.message ?? 'Just a quick heads up — this call may be recorded for quality.',
          applyToInbound: dc.applyToInbound ?? false,
          applyToOutbound: dc.applyToOutbound ?? true,
        })
      }
      // DNC entries are loaded on Voice tab open via loadDncEntries below.
      loadDncEntries().catch(() => {})
    } catch (error) {
      console.error('Error loading voice settings:', error)
    } finally {
      setLoadingVoice(false)
    }
  }

  // Mirror of the server-side voicemail template (VoiceService.saveVoiceConfig).
  // If the server template changes, update this too — or expose it via API later.
  const buildVoicemailMessage = (agentName: string, firmShortName: string) => {
    const a = agentName?.trim() || 'your AI receptionist'
    const f = firmShortName?.trim() || orgData.name?.trim() || 'our office'
    return `Hi there, this is ${a} with ${f}. We received your inquiry and wanted to reach out personally. Please give us a call back at this number — we would love to help. Talk soon!`
  }

  const handlePreviewVoicemail = async () => {
    if (previewingVoicemail) {
      voicemailAudioRef.current?.pause()
      voicemailAudioRef.current = null
      setPreviewingVoicemail(false)
      return
    }
    const text = buildVoicemailMessage(voiceSettings.assistantName, orgData.name)
    setPreviewingVoicemail(true)
    try {
      // Always use molly for the voicemail preview — that's the LegalLady
      // brand voice and matches what the AI voice card (just above) plays.
      // Older orgs may still have voiceConfig.voiceId set to 'lauren' from
      // before the molly default landed; we don't want the preview to
      // surface that drift to the user.
      const res = await api.post(
        '/api/voice/tts-preview',
        { text, voiceId: 'molly' },
        { responseType: 'blob' }
      )
      const url = URL.createObjectURL(res.data)
      const audio = new Audio(url)
      voicemailAudioRef.current = audio
      audio.onended = () => {
        setPreviewingVoicemail(false)
        URL.revokeObjectURL(url)
        voicemailAudioRef.current = null
      }
      audio.onerror = () => {
        setPreviewingVoicemail(false)
        URL.revokeObjectURL(url)
        voicemailAudioRef.current = null
      }
      await audio.play()
    } catch {
      setPreviewingVoicemail(false)
      toast({ title: 'Preview failed', description: 'Could not generate voicemail preview.', variant: 'destructive' })
    }
  }

  const handleSaveDisclosureConfig = async () => {
    setSavingDisclosure(true)
    try {
      await api.put('/api/voice/disclosure-config', disclosureConfig)
      toast({
        title: 'Saved',
        description: disclosureConfig.enabled
          ? 'Recording disclosure is active. Agents will rebuild on next call.'
          : 'Recording disclosure disabled.',
      })
    } catch (err: any) {
      toast({
        title: 'Error',
        description: err?.response?.data?.message || 'Failed to save recording disclosure',
        variant: 'destructive',
      })
    } finally {
      setSavingDisclosure(false)
    }
  }

  const loadDncEntries = async (search?: string) => {
    setDncLoading(true)
    try {
      const params: Record<string, string> = { limit: '100' }
      if (search) params.search = search
      const res = await api.get('/api/voice/dnc', { params })
      if (res.data?.success) {
        setDncEntries(res.data.entries || [])
        setDncTotal(res.data.total || 0)
      }
    } catch (err) {
      console.warn('Failed to load DNC entries:', err)
    } finally {
      setDncLoading(false)
    }
  }

  const handleAddDncEntry = async () => {
    const phone = dncNewNumber.trim()
    if (!phone) return
    setDncAdding(true)
    try {
      await api.post('/api/voice/dnc', {
        phoneNumber: phone,
        source: 'manual',
        notes: dncNewNotes.trim() || undefined,
      })
      setDncNewNumber('')
      setDncNewNotes('')
      await loadDncEntries(dncSearch.trim() || undefined)
      toast({ title: 'Added', description: `${phone} added to do-not-call list.` })
    } catch (err: any) {
      toast({
        title: 'Error',
        description: err?.response?.data?.message || 'Failed to add DNC entry',
        variant: 'destructive',
      })
    } finally {
      setDncAdding(false)
    }
  }

  const handleRemoveDncEntry = async (id: string, phoneNumber: string) => {
    try {
      await api.delete(`/api/voice/dnc/${id}`)
      setDncEntries(prev => prev.filter(e => e.id !== id))
      setDncTotal(prev => Math.max(prev - 1, 0))
      toast({ title: 'Removed', description: `${phoneNumber} removed from do-not-call list.` })
    } catch (err: any) {
      toast({
        title: 'Error',
        description: err?.response?.data?.message || 'Failed to remove DNC entry',
        variant: 'destructive',
      })
    }
  }

  const handleSaveVoiceSettings = async () => {
    setSavingVoice(true)
    try {
      // Save voice config (voice, tone, etc.) - also pushes to Telnyx/ElevenLabs
      await api.put('/api/voice/config', {
        voiceId: voiceConfig.voiceId,
        tonePreset: voiceConfig.tonePreset
      })

      // Update assistant name/greeting if changed (don't send voiceId - already handled above)
      if (assistants.length === 0) {
        toast({ title: 'Note', description: 'Voice config saved. Create an AI agent to apply name and greeting settings.' })
        setSavingVoice(false)
        return
      }
      if (voiceSettings.assistantName) {
        await api.put(`/api/voice/assistants/${assistants[0].id}`, {
          name: voiceSettings.assistantName || undefined,
        })
      }

      toast({
        title: 'Success',
        description: 'Voice settings saved. AI assistant has been updated.'
      })
    } catch (error: any) {
      console.error('Error saving voice settings:', error)
      toast({
        title: 'Error',
        description: error.response?.data?.message || 'Failed to save voice settings',
        variant: 'destructive'
      })
    } finally {
      setSavingVoice(false)
    }
  }

  const updateBookingConfig = (patch: Partial<typeof voiceConfig.bookingConfig>) => {
    setVoiceConfig(prev => ({
      ...prev,
      bookingConfig: {
        ...prev.bookingConfig,
        ...patch,
      },
    }))
  }

  const handleSaveCalendarConfig = async () => {
    setSavingCalendarConfig(true)
    try {
      const blackoutDates = (voiceConfig.bookingConfig.blackoutDates || [])
        .map(d => d.trim())
        .filter(Boolean)
      await api.put('/api/voice/config', {
        firmTimezone: voiceConfig.firmTimezone,
        businessHours: voiceConfig.businessHours,
        bookingConfig: {
          consultationLengthMinutes: voiceConfig.bookingConfig.consultationLengthMinutes,
          businessHoursOnly: voiceConfig.bookingConfig.businessHoursOnly,
          blackoutDates,
        },
      })
      setVoiceConfig(prev => ({
        ...prev,
        bookingConfig: { ...prev.bookingConfig, blackoutDates },
      }))
      toast({
        title: 'Calendar settings saved',
        description: 'Booking prompts and Legal Lady calendar checks will use these rules.',
      })
    } catch (error: any) {
      toast({
        title: 'Error',
        description: error.response?.data?.message || 'Failed to save calendar settings',
        variant: 'destructive',
      })
    } finally {
      setSavingCalendarConfig(false)
    }
  }

  const handleSaveCallbackConfig = async () => {
    setSavingCallback(true)
    try {
      await api.put('/api/voice/callback-config', callbackConfig)
      toast({
        title: 'Success',
        description: 'Callback settings saved.',
      })
    } catch (error: any) {
      console.error('Error saving callback config:', error)
      toast({
        title: 'Error',
        description: error.response?.data?.message || 'Failed to save callback settings',
        variant: 'destructive',
      })
    } finally {
      setSavingCallback(false)
    }
  }

  const handleSaveBookingReminderConfig = async () => {
    setSavingBookingReminder(true)
    try {
      await api.put('/api/voice/booking-reminder-config', bookingReminderConfig)
      toast({ title: 'Success', description: 'Booking reminder settings saved.' })
    } catch (error: any) {
      toast({ title: 'Error', description: error.response?.data?.message || 'Failed to save reminder settings', variant: 'destructive' })
    } finally {
      setSavingBookingReminder(false)
    }
  }

  // Resolve avatar URLs — relative paths need the API base URL prepended
  const resolveAvatarUrl = (url?: string) => {
    if (!url) return undefined
    if (url.startsWith('http') || url.startsWith('data:')) return url
    return `${API_BASE_URL}${url}`
  }

  const handleAvatarUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0]
    if (!file) return

    // Validate file type
    if (!file.type.match(/image\/(jpeg|jpg|png|gif|webp)/)) {
      toast({
        title: 'Error',
        description: 'Please upload an image file (JPG, PNG, GIF, or WebP)',
        variant: 'destructive'
      })
      return
    }

    // Validate file size (5MB)
    if (file.size > 5 * 1024 * 1024) {
      toast({
        title: 'Error',
        description: 'Image must be less than 5MB',
        variant: 'destructive'
      })
      return
    }

    setUploadingAvatar(true)
    try {
      const formData = new FormData()
      formData.append('avatar', file)

      // Upload file — this saves avatar_url to database directly
      const response = await api.post('/api/agent/avatar', formData, {
        headers: { 'Content-Type': undefined },
        timeout: 30000
      })

      const avatarUrl = response.data.avatarUrl
      // Update local display
      setProfileData(prev => ({ ...prev, avatarUrl: avatarUrl }))

      // Refresh auth state from server so header and all components update immediately
      await refreshAgent()

      toast({
        title: 'Photo saved',
        description: 'Your profile picture has been updated'
      })

      // Reset file input
      if (fileInputRef.current) {
        fileInputRef.current.value = ''
      }
    } catch (error: any) {
      toast({
        title: 'Upload failed',
        description: error.response?.data?.message || 'Failed to upload photo. Please try again.',
        variant: 'destructive'
      })
    } finally {
      setUploadingAvatar(false)
    }
  }

  const handleUpdateProfile = async () => {
    setLoading(true)
    try {
      const result = await updateProfile({
        name: profileData.name,
        email: profileData.email,
        department: profileData.department,
        timezone: profileData.timezone,
      } as any)

      if (result?.success) {
        toast({
          title: 'Success',
          description: 'Profile updated successfully'
        })
      } else {
        throw new Error(result?.message || 'Failed to update profile')
      }
    } catch (error: any) {
      console.error('Error updating profile:', error)
      toast({
        title: 'Error',
        description: error.message || 'Failed to update profile',
        variant: 'destructive'
      })
    } finally {
      setLoading(false)
    }
  }

  const handleDeleteAccount = async () => {
    setDeleteError('')
    setDeleteLoading(true)
    try {
      await api.post('/api/auth/delete-account', {
        password: deletePassword,
        organizationName: deleteOrgName
      })
      // Account deleted — clear local auth state so /login doesn't bounce
      // back to the dashboard on a stale token, then redirect with a flag
      // the login page uses to show a confirmation banner.
      localStorage.removeItem('app_auth_token')
      localStorage.removeItem('app_agent_data')
      localStorage.removeItem('app_onboarding_complete')
      delete api.defaults.headers.common['Authorization']
      window.location.href = '/login?deleted=1'
    } catch (err: any) {
      setDeleteError(err.response?.data?.message || 'Failed to delete account')
      setDeleteLoading(false)
    }
  }

  const handleChangePassword = async () => {
    if (!passwordData.currentPassword) {
      toast({ title: 'Error', description: 'Please enter your current password', variant: 'destructive' })
      return
    }
    if (passwordData.newPassword.length < 8) {
      toast({ title: 'Error', description: 'New password must be at least 8 characters', variant: 'destructive' })
      return
    }
    if (!/[A-Z]/.test(passwordData.newPassword) || !/[a-z]/.test(passwordData.newPassword) || !/\d/.test(passwordData.newPassword)) {
      toast({ title: 'Error', description: 'Password must include uppercase, lowercase, and a number', variant: 'destructive' })
      return
    }
    if (passwordData.newPassword !== passwordData.confirmPassword) {
      toast({
        title: 'Error',
        description: 'Passwords do not match',
        variant: 'destructive'
      })
      return
    }

    setLoading(true)
    try {
      await api.post('/api/auth/change-password', {
        currentPassword: passwordData.currentPassword,
        newPassword: passwordData.newPassword
      })

      toast({
        title: 'Success',
        description: 'Password changed successfully'
      })

      setPasswordData({
        currentPassword: '',
        newPassword: '',
        confirmPassword: ''
      })
    } catch (error: any) {
      console.error('Error changing password:', error)
      toast({
        title: 'Error',
        description: error.response?.data?.message || 'Failed to change password',
        variant: 'destructive'
      })
    } finally {
      setLoading(false)
    }
  }

  const getInitials = (name: string) => {
    return name
      .split(' ')
      .map(n => n[0])
      .join('')
      .toUpperCase()
      .slice(0, 2)
  }

  const isSuperAdmin = (agent as any)?.isSuperAdmin
  const getRoleBadge = (role: string) => {
    const colors: Record<string, string> = {
      admin: 'bg-brand-secondary text-white',
      supervisor: 'bg-brand-primary text-white',
      agent: 'bg-brand-primary-light text-brand-primary'
    }
    return (
      <div className="flex flex-wrap items-center gap-1.5">
        <Badge className={colors[role] || colors.agent}>
          {role.charAt(0).toUpperCase() + role.slice(1)}
        </Badge>
        {isSuperAdmin && (
          <Badge className="border-transparent bg-[#ede9fe] dark:bg-violet-900/40 text-violet-600 dark:text-violet-300 shadow-sm whitespace-nowrap">
            Super Admin
          </Badge>
        )}
      </div>
    )
  }

  return (
    <div className="">
      {/* Hero header — account chip with avatar, role, email */}
      <div className="relative overflow-hidden rounded-2xl border bg-card shadow-sm card-inset mb-6">
        <div className="pointer-events-none absolute -top-20 -right-20 h-56 w-56 rounded-full bg-gradient-to-br from-brand-periwinkle/20 via-brand-royal/10 to-transparent blur-3xl" aria-hidden="true" />
        <div className="pointer-events-none absolute -bottom-24 -left-16 h-48 w-48 rounded-full bg-gradient-to-tr from-brand-royal/10 to-transparent blur-3xl" aria-hidden="true" />
        <div className="relative p-5 sm:p-7 flex flex-col sm:flex-row sm:items-center gap-4 sm:gap-6">
          <Avatar className="w-16 h-16 sm:w-20 sm:h-20 ring-2 ring-background shadow-lg shrink-0">
            <AvatarImage src={resolveAvatarUrl(profileData.avatarUrl)} alt={profileData.name} />
            <AvatarFallback className="bg-gradient-to-br from-brand-royal to-brand-periwinkle text-white text-xl sm:text-2xl font-bold">
              {getInitials(profileData.name || agent?.name || '')}
            </AvatarFallback>
          </Avatar>
          <div className="flex-1 min-w-0">
            <p className="text-[10px] font-bold uppercase tracking-[0.18em] text-muted-foreground mb-1">Settings</p>
            <h1 className="text-2xl sm:text-3xl font-heading font-bold tracking-tight bg-gradient-to-br from-foreground to-foreground/70 bg-clip-text text-transparent">
              {profileData.name || agent?.name || 'Your account'}
            </h1>
            <div className="flex flex-wrap items-center gap-2 mt-2 text-sm text-muted-foreground">
              {profileData.email && (
                <span className="inline-flex items-center gap-1.5">
                  <Mail className="h-3.5 w-3.5" />
                  <span className="truncate">{profileData.email}</span>
                </span>
              )}
              {agent?.role && (
                <>
                  <span className="text-muted-foreground/50">·</span>
                  <span className="inline-flex items-center gap-1.5">
                    <Shield className="h-3.5 w-3.5" />
                    <span className="capitalize">{agent.role}</span>
                    {isSuperAdmin && <span className="ml-1 text-violet-500 dark:text-violet-400 font-semibold">· Super Admin</span>}
                  </span>
                </>
              )}
            </div>
          </div>
        </div>
      </div>

      <Tabs defaultValue="profile" className="space-y-6">
        {/* Horizontal-scroll wrapper for mobile. The wrapper itself owns the
            scroll (not TabsList) so iOS Safari treats it as the touch target
            for horizontal pan gestures. touch-pan-x prevents the vertical
            page scroll from hijacking horizontal swipes. */}
        <div className="relative -mx-4 sm:mx-0 px-4 sm:px-0 overflow-x-auto sm:overflow-visible touch-pan-x [&::-webkit-scrollbar]:hidden [-ms-overflow-style:none] [scrollbar-width:none]">
          <TabsList className="glass w-max sm:w-full h-auto flex sm:flex-wrap sm:justify-start gap-1 p-1.5">
          <TabsTrigger value="profile" className="justify-start sm:justify-center data-[state=active]:bg-brand-primary data-[state=active]:text-white">
            <User className="w-4 h-4 mr-2 shrink-0" />
            Profile
          </TabsTrigger>
          <TabsTrigger value="security" className="justify-start sm:justify-center data-[state=active]:bg-brand-primary data-[state=active]:text-white">
            <Lock className="w-4 h-4 mr-2 shrink-0" />
            Security
          </TabsTrigger>
          <TabsTrigger value="notifications" className="justify-start sm:justify-center data-[state=active]:bg-brand-primary data-[state=active]:text-white">
            <Bell className="w-4 h-4 mr-2 shrink-0" />
            Alerts
          </TabsTrigger>
          {(agent?.role === 'admin' || isImpersonating) && (
            <>
              <TabsTrigger value="organization" className="justify-start sm:justify-center data-[state=active]:bg-brand-primary data-[state=active]:text-white">
                <Building2 className="w-4 h-4 mr-2 shrink-0" />
                Organization
              </TabsTrigger>
              <TooltipProvider delayDuration={100}>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <TabsTrigger value="voice" className={`justify-start sm:justify-center data-[state=active]:bg-brand-primary data-[state=active]:text-white ${voiceLocked ? 'opacity-60' : ''}`}>
                      <Phone className="w-4 h-4 mr-2 shrink-0" />
                      Voice
                      {voiceLocked && <Lock className="w-3 h-3 ml-1.5 opacity-70" />}
                    </TabsTrigger>
                  </TooltipTrigger>
                  {voiceLocked && (
                    <TooltipContent>
                      <p className="text-xs">Voice settings unlock on the <span className="font-semibold">Partner</span> plan.</p>
                    </TooltipContent>
                  )}
                </Tooltip>
              </TooltipProvider>
              <TabsTrigger value="widget" className="justify-start sm:justify-center data-[state=active]:bg-brand-primary data-[state=active]:text-white">
                <Code className="w-4 h-4 mr-2 shrink-0" />
                Chat Widget
              </TabsTrigger>
              <TooltipProvider delayDuration={100}>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <TabsTrigger value="lead-followup" className={`justify-start sm:justify-center data-[state=active]:bg-brand-primary data-[state=active]:text-white ${followUpLocked ? 'opacity-60' : ''}`}>
                      <Zap className="w-4 h-4 mr-2 shrink-0" />
                      Lead Follow-Up
                      {followUpLocked && <Lock className="w-3 h-3 ml-1.5 opacity-70" />}
                    </TabsTrigger>
                  </TooltipTrigger>
                  {followUpLocked && (
                    <TooltipContent>
                      <p className="text-xs">Lead Follow-Up unlocks on the <span className="font-semibold">Principal</span> plan — proactive outreach on every web inquiry.</p>
                    </TooltipContent>
                  )}
                </Tooltip>
              </TooltipProvider>
            </>
          )}
        </TabsList>
        </div>

        {/* Profile Tab */}
        <TabsContent value="profile" className="space-y-5">
          {/* Profile photo card */}
          <div className="rounded-xl border bg-card shadow-sm card-inset overflow-hidden">
            <div className="p-5 sm:p-6">
              <div className="flex items-center gap-2 mb-1">
                <div className="h-7 w-7 rounded-lg bg-brand-periwinkle/15 flex items-center justify-center">
                  <Upload className="h-3.5 w-3.5 text-brand-periwinkle" />
                </div>
                <h3 className="text-base font-semibold">Profile photo</h3>
              </div>
              <p className="text-sm text-muted-foreground mb-5 ml-9">A photo helps teammates recognize you in chats and assignments.</p>

              <div className="flex flex-col sm:flex-row items-center sm:items-start gap-5 sm:gap-6 sm:ml-9">
                <div className="relative group">
                  <Avatar className="w-24 h-24 ring-2 ring-background shadow-md">
                    <AvatarImage src={resolveAvatarUrl(profileData.avatarUrl)} alt={profileData.name} />
                    <AvatarFallback className="bg-gradient-to-br from-brand-royal to-brand-periwinkle text-white text-3xl font-bold">
                      {getInitials(profileData.name)}
                    </AvatarFallback>
                  </Avatar>
                  <button
                    type="button"
                    onClick={() => fileInputRef.current?.click()}
                    disabled={uploadingAvatar}
                    aria-label="Change photo"
                    className="absolute inset-0 rounded-full bg-foreground/60 backdrop-blur-sm opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center text-background"
                  >
                    {uploadingAvatar ? <Loader2 className="h-5 w-5 animate-spin" /> : <Upload className="h-5 w-5" />}
                  </button>
                </div>
                <div className="flex-1 text-center sm:text-left">
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/jpeg,image/jpg,image/png,image/gif,image/webp"
                    onChange={handleAvatarUpload}
                    className="hidden"
                  />
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => fileInputRef.current?.click()}
                    disabled={uploadingAvatar}
                  >
                    {uploadingAvatar ? (
                      <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Uploading…</>
                    ) : (
                      <><Upload className="w-4 h-4 mr-2" /> Change photo</>
                    )}
                  </Button>
                  <p className="text-xs text-muted-foreground mt-2">JPG, PNG, GIF, or WEBP · max 5MB</p>
                </div>
              </div>
            </div>
          </div>

          {/* Personal details card */}
          <div className="rounded-xl border bg-card shadow-sm card-inset overflow-hidden">
            <div className="p-5 sm:p-6">
              <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3 mb-5">
                <div>
                  <div className="flex items-center gap-2 mb-1">
                    <div className="h-7 w-7 rounded-lg bg-brand-royal/15 flex items-center justify-center">
                      <User className="h-3.5 w-3.5 text-brand-royal" />
                    </div>
                    <h3 className="text-base font-semibold">Personal details</h3>
                  </div>
                  <p className="text-sm text-muted-foreground ml-9">Your name, email, department, and time zone.</p>
                </div>
                <Button
                  onClick={handleUpdateProfile}
                  disabled={loading}
                  className="bg-gradient-to-r from-brand-royal to-brand-periwinkle text-white hover:opacity-90 shadow-md shadow-brand-periwinkle/20 shrink-0 w-full sm:w-auto"
                >
                  {loading ? (
                    <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Saving…</>
                  ) : (
                    <><Save className="w-4 h-4 mr-2" /> Save profile</>
                  )}
                </Button>
              </div>

              <div className="grid gap-4 sm:grid-cols-2">
                <div className="grid gap-1.5">
                  <Label htmlFor="name" className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                    <User className="h-3 w-3" /> Full name
                  </Label>
                  <Input
                    id="name"
                    value={profileData.name}
                    onChange={(e) => setProfileData({ ...profileData, name: e.target.value })}
                    placeholder="Jane Doe"
                  />
                </div>

                <div className="grid gap-1.5">
                  <Label htmlFor="email" className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                    <Mail className="h-3 w-3" /> Email
                  </Label>
                  <Input
                    id="email"
                    type="email"
                    value={profileData.email}
                    onChange={(e) => setProfileData({ ...profileData, email: e.target.value })}
                    placeholder="you@firm.com"
                  />
                </div>

                <div className="grid gap-1.5">
                  <Label htmlFor="department" className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                    <Briefcase className="h-3 w-3" /> Department
                  </Label>
                  <Input
                    id="department"
                    value={profileData.department}
                    onChange={(e) => setProfileData({ ...profileData, department: e.target.value })}
                    placeholder="e.g., Intake, Litigation"
                  />
                </div>

                <div className="grid gap-1.5">
                  <Label htmlFor="user-timezone" className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                    <Globe2 className="h-3 w-3" /> Time zone
                  </Label>
                  <Select
                    value={profileData.timezone}
                    onValueChange={(v) => setProfileData({ ...profileData, timezone: v })}
                  >
                    <SelectTrigger id="user-timezone">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {US_TIMEZONES.map(tz => (
                        <SelectItem key={tz.value} value={tz.value}>{tz.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <p className="text-xs text-muted-foreground mt-3 leading-relaxed">
                Booking times, reminders, and notifications display in your time zone. The firm's overall time zone is set separately by an admin.
              </p>
            </div>
          </div>

          {/* Role & access card */}
          <div className="rounded-xl border bg-card shadow-sm card-inset overflow-hidden">
            <div className="p-5 sm:p-6 flex flex-col sm:flex-row sm:items-center gap-4">
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-1">
                  <div className="h-7 w-7 rounded-lg bg-violet-500/15 flex items-center justify-center">
                    <Shield className="h-3.5 w-3.5 text-violet-500" />
                  </div>
                  <h3 className="text-base font-semibold">Role & access</h3>
                </div>
                <div className="flex flex-wrap items-center gap-2 ml-9 mt-2">
                  {agent?.role && getRoleBadge(agent.role)}
                  <span className="text-sm text-muted-foreground">
                    {(agent?.role === 'admin' || isImpersonating)
                      ? 'Full admin access — manage teammates from the Team page.'
                      : 'Contact an admin to change your role.'}
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* Team Management Card (admin only) */}
          {(agent?.role === 'admin' || isImpersonating) && (
            <Link
              to="/team"
              className="block rounded-xl border bg-card shadow-sm card-inset overflow-hidden hover:border-brand-periwinkle/40 hover:bg-brand-periwinkle/[0.02] transition-colors group"
            >
              <div className="p-5 sm:p-6 flex items-center gap-4">
                <div className="h-12 w-12 rounded-xl bg-gradient-to-br from-brand-royal/15 to-brand-periwinkle/15 flex items-center justify-center shrink-0">
                  <Users2 className="h-5 w-5 text-brand-royal" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-base font-semibold">Manage your team</p>
                  <p className="text-sm text-muted-foreground mt-0.5">Invite teammates, assign roles, and review permissions.</p>
                </div>
                <ChevronRight className="h-5 w-5 text-muted-foreground group-hover:text-brand-periwinkle transition-colors shrink-0" />
              </div>
            </Link>
          )}

          {/* Mobile bottom nav personalization */}
          <MobileNavSettings />
        </TabsContent>

        {/* Security Tab */}
        <TabsContent value="security" className="space-y-5">
          {/* Password card */}
          <div className="rounded-xl border bg-card shadow-sm card-inset overflow-hidden">
            <div className="p-5 sm:p-6">
              <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3 mb-5">
                <div>
                  <div className="flex items-center gap-2 mb-1">
                    <div className="h-7 w-7 rounded-lg bg-brand-royal/15 flex items-center justify-center">
                      <Lock className="h-3.5 w-3.5 text-brand-royal" />
                    </div>
                    <h3 className="text-base font-semibold">Change password</h3>
                  </div>
                  <p className="text-sm text-muted-foreground ml-9">Use a strong, unique password you don't share with other services.</p>
                </div>
                <Button
                  onClick={handleChangePassword}
                  disabled={loading}
                  className="bg-gradient-to-r from-brand-royal to-brand-periwinkle text-white hover:opacity-90 shadow-md shadow-brand-periwinkle/20 shrink-0 w-full sm:w-auto"
                >
                  {loading ? (
                    <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Updating…</>
                  ) : (
                    <><Lock className="w-4 h-4 mr-2" /> Update password</>
                  )}
                </Button>
              </div>

              <div className="grid gap-4 sm:grid-cols-2">
                <div className="grid gap-1.5 sm:col-span-2">
                  <Label htmlFor="current-password" className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Current password</Label>
                  <Input
                    id="current-password"
                    type="password"
                    autoComplete="current-password"
                    value={passwordData.currentPassword}
                    onChange={(e) => setPasswordData({ ...passwordData, currentPassword: e.target.value })}
                  />
                </div>

                <div className="grid gap-1.5">
                  <Label htmlFor="new-password" className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">New password</Label>
                  <Input
                    id="new-password"
                    type="password"
                    autoComplete="new-password"
                    value={passwordData.newPassword}
                    onChange={(e) => setPasswordData({ ...passwordData, newPassword: e.target.value })}
                  />
                </div>

                <div className="grid gap-1.5">
                  <Label htmlFor="confirm-password" className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Confirm new password</Label>
                  <Input
                    id="confirm-password"
                    type="password"
                    autoComplete="new-password"
                    value={passwordData.confirmPassword}
                    onChange={(e) => setPasswordData({ ...passwordData, confirmPassword: e.target.value })}
                  />
                </div>
              </div>
              <p className="text-xs text-muted-foreground mt-3">8+ characters with at least one uppercase, one lowercase, and one number.</p>
            </div>
          </div>

          {/* Danger Zone */}
          {agent?.role === 'admin' && (
            <div className="relative overflow-hidden rounded-xl border border-red-300/60 dark:border-red-900/60 bg-gradient-to-br from-red-50/60 via-card to-card dark:from-red-950/20 dark:via-card dark:to-card shadow-sm">
              <div className="absolute top-0 left-0 h-full w-1 bg-gradient-to-b from-red-500 to-red-600" aria-hidden="true" />
              <div className="p-5 sm:p-6">
                <div className="flex items-center gap-2 mb-1">
                  <div className="h-7 w-7 rounded-lg bg-red-500/15 flex items-center justify-center">
                    <AlertTriangle className="h-3.5 w-3.5 text-red-600 dark:text-red-400" />
                  </div>
                  <h3 className="text-base font-semibold text-red-700 dark:text-red-400">Danger zone</h3>
                </div>
                <p className="text-sm text-muted-foreground ml-9 mb-5">Permanently delete this organization and every record we hold for it. This cannot be undone.</p>

                <div className="ml-9">
                {deleteStep === 0 && (
                  <Button
                    variant="outline"
                    className="border-red-300 text-red-600 hover:bg-red-50 dark:border-red-800 dark:hover:bg-red-950/20"
                    onClick={() => setDeleteStep(1)}
                  >
                    <Trash2 className="w-4 h-4 mr-2" />
                    Delete Account & All Data
                  </Button>
                )}

                {deleteStep === 1 && (
                  <div className="space-y-4 max-w-md">
                    <p className="text-sm text-muted-foreground">
                      <strong>Step 1 of 3:</strong> Enter your password to verify your identity.
                    </p>
                    <div className="grid gap-2">
                      <Label htmlFor="delete-password">Your Password</Label>
                      <Input
                        id="delete-password"
                        type="password"
                        value={deletePassword}
                        onChange={(e) => setDeletePassword(e.target.value)}
                        placeholder="Enter your password"
                      />
                    </div>
                    {deleteError && <p className="text-sm text-red-600">{deleteError}</p>}
                    <div className="flex gap-2">
                      <Button variant="outline" onClick={() => { setDeleteStep(0); setDeletePassword(''); setDeleteError('') }}>Cancel</Button>
                      <Button
                        className="bg-red-600 hover:bg-red-700 text-white"
                        onClick={() => {
                          if (!deletePassword) { setDeleteError('Password is required'); return }
                          setDeleteError('')
                          setDeleteStep(2)
                        }}
                      >
                        Continue
                      </Button>
                    </div>
                  </div>
                )}

                {deleteStep === 2 && (
                  <div className="space-y-4 max-w-md">
                    <p className="text-sm text-muted-foreground">
                      <strong>Step 2 of 3:</strong> Type your organization name to confirm: <strong className="text-foreground">{orgData.name || 'your organization'}</strong>
                    </p>
                    <div className="grid gap-2">
                      <Label htmlFor="delete-org-name">Organization Name</Label>
                      <Input
                        id="delete-org-name"
                        value={deleteOrgName}
                        onChange={(e) => setDeleteOrgName(e.target.value)}
                        placeholder="Type organization name exactly"
                      />
                    </div>
                    {deleteError && <p className="text-sm text-red-600">{deleteError}</p>}
                    <div className="flex gap-2">
                      <Button variant="outline" onClick={() => { setDeleteStep(0); setDeletePassword(''); setDeleteOrgName(''); setDeleteError('') }}>Cancel</Button>
                      <Button
                        className="bg-red-600 hover:bg-red-700 text-white"
                        onClick={() => {
                          if (!deleteOrgName) { setDeleteError('Organization name is required'); return }
                          setDeleteError('')
                          setDeleteStep(3)
                        }}
                      >
                        Continue
                      </Button>
                    </div>
                  </div>
                )}

                {deleteStep === 3 && (
                  <div className="space-y-4 max-w-md">
                    <div className="bg-red-50 dark:bg-red-950/30 border border-red-200 dark:border-red-900 rounded-lg p-4">
                      <p className="text-sm font-semibold text-red-700 dark:text-red-400 mb-2">This will permanently delete:</p>
                      <ul className="text-sm text-red-600 dark:text-red-400 space-y-1 list-disc list-inside">
                        <li>All call recordings and transcripts</li>
                        <li>All chat conversations and messages</li>
                        <li>All bookings, analytics, and lead data</li>
                        <li>All AI agents and phone numbers</li>
                        <li>All team member accounts</li>
                        <li>Your organization settings and integrations</li>
                      </ul>
                    </div>
                    {deleteError && <p className="text-sm text-red-600">{deleteError}</p>}
                    <div className="flex flex-col sm:flex-row gap-2">
                      <Button
                        variant="outline"
                        className="w-full sm:w-auto"
                        onClick={() => { setDeleteStep(0); setDeletePassword(''); setDeleteOrgName(''); setDeleteError('') }}
                      >
                        Cancel
                      </Button>
                      <Button
                        className="w-full sm:w-auto bg-red-600 hover:bg-red-700 text-white"
                        disabled={deleteLoading}
                        onClick={handleDeleteAccount}
                      >
                        <Trash2 className="w-4 h-4 mr-2" />
                        {deleteLoading ? 'Deleting...' : 'Permanently Delete Everything'}
                      </Button>
                    </div>
                  </div>
                )}
                </div>
              </div>
            </div>
          )}
        </TabsContent>

        {/* Alerts Tab */}
        <TabsContent value="notifications" className="space-y-5">
          <div className="rounded-xl border bg-card shadow-sm card-inset overflow-hidden">
            <div className="p-5 sm:p-6">
              <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3 mb-5">
                <div>
                  <div className="flex items-center gap-2 mb-1">
                    <div className="h-7 w-7 rounded-lg bg-amber-500/15 flex items-center justify-center">
                      <Bell className="h-3.5 w-3.5 text-amber-600 dark:text-amber-400" />
                    </div>
                    <h3 className="text-base font-semibold">Alerts</h3>
                  </div>
                  <p className="text-sm text-muted-foreground ml-9">Choose how you want to be alerted for each kind of event.</p>
                </div>
                <Button
                  className="bg-gradient-to-r from-brand-royal to-brand-periwinkle text-white hover:opacity-90 shadow-md shadow-brand-periwinkle/20 shrink-0 w-full sm:w-auto"
                  onClick={handleSaveNotifications}
                  disabled={savingNotifications}
                >
                  {savingNotifications ? (
                    <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Saving…</>
                  ) : (
                    <><Save className="w-4 h-4 mr-2" /> Save preferences</>
                  )}
                </Button>
              </div>
              <div className="space-y-6">
              {/* Master switches */}
              <div className="space-y-4 pb-4 border-b border-border">
                <div className="flex items-center justify-between">
                  <div className="space-y-0.5">
                    <Label htmlFor="sound-enabled" className="text-base">Sound</Label>
                    <p className="text-sm text-muted-foreground">Master mute for all alert sounds</p>
                  </div>
                  <Switch
                    id="sound-enabled"
                    checked={notificationSettings.soundEnabled}
                    onCheckedChange={(checked) => setNotificationSettings({ ...notificationSettings, soundEnabled: checked })}
                  />
                </div>
                <div className="flex items-center justify-between">
                  <div className="space-y-0.5">
                    <Label htmlFor="desktop-notifications" className="text-base">Desktop Popups</Label>
                    <p className="text-sm text-muted-foreground">Master toggle for browser notifications</p>
                  </div>
                  <Switch
                    id="desktop-notifications"
                    checked={notificationSettings.desktopNotifications}
                    onCheckedChange={(checked) => setNotificationSettings({ ...notificationSettings, desktopNotifications: checked })}
                  />
                </div>
              </div>

              {/* Per-event matrix */}
              <div>
                <h4 className="text-base font-semibold mb-3">Per-event alerts</h4>
                <p className="text-xs text-muted-foreground mb-4">Fine-tune which channels each event uses. Master switches above override these.</p>

                {/* Column headers (hidden on mobile — labels show inline instead) */}
                <div className="hidden sm:grid grid-cols-[1fr,auto,auto,auto] gap-4 pb-2 border-b border-border text-xs uppercase tracking-wide text-muted-foreground">
                  <div>Event</div>
                  <div className="w-16 text-center">Sound</div>
                  <div className="w-16 text-center">Desktop</div>
                  <div className="w-20 text-center">Email</div>
                </div>

                <div className="divide-y divide-border">
                  {/* New chat message */}
                  <AlertRow
                    title="New chat message"
                    desc="Visitor sends a message in chat"
                    sound={{ checked: notificationSettings.newMessageSound, onChange: (v) => setNotificationSettings(s => ({ ...s, newMessageSound: v })), disabled: !notificationSettings.soundEnabled }}
                    desktop={{ checked: notificationSettings.newMessageDesktop, onChange: (v) => setNotificationSettings(s => ({ ...s, newMessageDesktop: v })), disabled: !notificationSettings.desktopNotifications }}
                  />

                  {/* New chat request */}
                  <AlertRow
                    title="New chat request"
                    desc="A new visitor arrives on your website and opens chat"
                    sound={{ checked: notificationSettings.chatRequestSound, onChange: (v) => setNotificationSettings(s => ({ ...s, chatRequestSound: v })), disabled: !notificationSettings.soundEnabled }}
                    desktop={{ checked: notificationSettings.chatRequestDesktop, onChange: (v) => setNotificationSettings(s => ({ ...s, chatRequestDesktop: v })), disabled: !notificationSettings.desktopNotifications }}
                  />

                  {/* Human escalation */}
                  <AlertRow
                    title="Human escalation"
                    desc="Visitor specifically asks to speak with a real person"
                    sound={{ checked: notificationSettings.humanEscalationSound, onChange: (v) => setNotificationSettings(s => ({ ...s, humanEscalationSound: v })), disabled: !notificationSettings.soundEnabled }}
                    desktop={{ checked: notificationSettings.humanEscalationDesktop, onChange: (v) => setNotificationSettings(s => ({ ...s, humanEscalationDesktop: v })), disabled: !notificationSettings.desktopNotifications }}
                    email={{ checked: notificationSettings.humanEscalationEmail, onChange: (v) => setNotificationSettings(s => ({ ...s, humanEscalationEmail: v })) }}
                  />

                  {/* Team DM */}
                  <AlertRow
                    title="Team message"
                    desc="A teammate sends you a direct message"
                    sound={{ checked: notificationSettings.teamMessageSound, onChange: (v) => setNotificationSettings(s => ({ ...s, teamMessageSound: v })), disabled: !notificationSettings.soundEnabled }}
                    desktop={{ checked: notificationSettings.teamMessageDesktop, onChange: (v) => setNotificationSettings(s => ({ ...s, teamMessageDesktop: v })), disabled: !notificationSettings.desktopNotifications }}
                  />

                  {/* New booking created */}
                  <AlertRow
                    title="New booking created"
                    desc="A booking is made from chat, a call, or a task"
                    sound={{ checked: notificationSettings.bookingCreatedSound, onChange: (v) => setNotificationSettings(s => ({ ...s, bookingCreatedSound: v })), disabled: !notificationSettings.soundEnabled }}
                    desktop={{ checked: notificationSettings.bookingCreatedDesktop, onChange: (v) => setNotificationSettings(s => ({ ...s, bookingCreatedDesktop: v })), disabled: !notificationSettings.desktopNotifications }}
                    email={{ checked: notificationSettings.bookingCreatedEmail, onChange: (v) => setNotificationSettings(s => ({ ...s, bookingCreatedEmail: v })) }}
                  />

                  {/* Booking reminder */}
                  <AlertRow
                    title="Booking / task reminder"
                    desc="Fires a few minutes before an appointment (see timing in the firm defaults section below)"
                    sound={{ checked: notificationSettings.bookingReminderSound, onChange: (v) => setNotificationSettings(s => ({ ...s, bookingReminderSound: v })), disabled: !notificationSettings.soundEnabled }}
                    desktop={{ checked: notificationSettings.bookingReminderDesktop, onChange: (v) => setNotificationSettings(s => ({ ...s, bookingReminderDesktop: v })), disabled: !notificationSettings.desktopNotifications }}
                    email={{ checked: notificationSettings.bookingReminderEmail, onChange: (v) => setNotificationSettings(s => ({ ...s, bookingReminderEmail: v })) }}
                  />

                  {/* Callback retries exhausted — auto-callback is a Principal feature */}
                  <AlertRow
                    title="Callback retries exhausted"
                    desc="Auto-callback gave up after all retries for a dropped inbound call"
                    sound={{ checked: notificationSettings.callbackExhaustedSound, onChange: (v) => setNotificationSettings(s => ({ ...s, callbackExhaustedSound: v })), disabled: !notificationSettings.soundEnabled }}
                    desktop={{ checked: notificationSettings.callbackExhaustedDesktop, onChange: (v) => setNotificationSettings(s => ({ ...s, callbackExhaustedDesktop: v })), disabled: !notificationSettings.desktopNotifications }}
                    tierGate={{ locked: !featureGate.isAllowed('auto_callback'), tier: 'principal' }}
                  />

                  {/* Affiliate inbound email — only shown to users who have a
                      partner inbox. */}
                  {isAffiliate && (
                    <AlertRow
                      title="Affiliate inbound email"
                      desc="A prospect or partner replies to your affiliate alias"
                      sound={{ checked: notificationSettings.affiliateEmailSound, onChange: (v) => setNotificationSettings(s => ({ ...s, affiliateEmailSound: v })), disabled: !notificationSettings.soundEnabled }}
                      desktop={{ checked: notificationSettings.affiliateEmailDesktop, onChange: (v) => setNotificationSettings(s => ({ ...s, affiliateEmailDesktop: v })), disabled: !notificationSettings.desktopNotifications }}
                    />
                  )}

                  {/* Call lead summary (admin only) — voice summary requires Partner+ */}
                  {(agent?.role === 'admin' || isImpersonating) && (
                    <AlertRow
                      title="Post-call lead summary (admin only)"
                      desc="Email digest sent after each completed call — includes transcript, next steps, and callback time"
                      email={{ checked: notificationSettings.leadSummaryEmail, onChange: (v) => setNotificationSettings(s => ({ ...s, leadSummaryEmail: v })) }}
                      tierGate={{ locked: !featureGate.isAllowed('call_summaries'), tier: 'partner' }}
                    />
                  )}
                </div>
              </div>
              </div>
            </div>
          </div>

          {/* Booking & Appointments — master defaults */}
          <div className="rounded-xl border bg-card shadow-sm card-inset overflow-hidden">
            <div className="p-5 sm:p-6">
              <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3 mb-5">
                <div>
                  <div className="flex items-center gap-2 mb-1">
                    <div className="h-7 w-7 rounded-lg bg-brand-periwinkle/15 flex items-center justify-center">
                      <PhoneCall className="h-3.5 w-3.5 text-brand-periwinkle" />
                    </div>
                    <h3 className="text-base font-semibold">Bookings, appointments & tasks</h3>
                  </div>
                  <p className="text-sm text-muted-foreground ml-9">
                    Default reminder settings for items from chat, calls, and tasks you add manually. Override per item in the Bookings calendar.
                  </p>
                </div>
                <Button
                  onClick={handleSaveBookingReminderConfig}
                  disabled={savingBookingReminder}
                  className="bg-gradient-to-r from-brand-royal to-brand-periwinkle text-white hover:opacity-90 shadow-md shadow-brand-periwinkle/20 shrink-0 w-full sm:w-auto"
                >
                  {savingBookingReminder ? (
                    <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Saving…</>
                  ) : (
                    <><Save className="w-4 h-4 mr-2" /> Save booking settings</>
                  )}
                </Button>
              </div>
              <div className="space-y-6">
              {/* Staff Reminders Section */}
              <div>
                <h4 className="text-sm font-semibold mb-3">Staff Reminders</h4>
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <div className="space-y-0.5">
                      <Label className="text-base">Reminder Enabled</Label>
                      <p className="text-sm text-muted-foreground">
                        Alert your team before scheduled bookings and tasks with a notification bell
                      </p>
                    </div>
                    <Switch
                      checked={bookingReminderConfig.attorneyReminderEnabled}
                      onCheckedChange={(checked) => setBookingReminderConfig(prev => ({ ...prev, attorneyReminderEnabled: checked }))}
                    />
                  </div>

                  <div className="flex items-center justify-between">
                    <div className="space-y-0.5">
                      <Label className={!bookingReminderConfig.attorneyReminderEnabled ? 'text-base text-muted-foreground' : 'text-base'}>
                        Reminder Sound
                      </Label>
                      <p className="text-sm text-muted-foreground">Play a chime along with the notification</p>
                    </div>
                    <Switch
                      checked={bookingReminderConfig.reminderSoundEnabled}
                      onCheckedChange={(checked) => setBookingReminderConfig(prev => ({ ...prev, reminderSoundEnabled: checked }))}
                      disabled={!bookingReminderConfig.attorneyReminderEnabled}
                    />
                  </div>

                  <div className="grid gap-2">
                    <Label className={!bookingReminderConfig.attorneyReminderEnabled ? 'text-muted-foreground' : ''}>
                      Remind team
                    </Label>
                    <Select
                      value={String(bookingReminderConfig.attorneyReminderMinutes)}
                      onValueChange={(v) => setBookingReminderConfig(prev => ({ ...prev, attorneyReminderMinutes: parseInt(v) }))}
                      disabled={!bookingReminderConfig.attorneyReminderEnabled}
                    >
                      <SelectTrigger className="w-48">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="5">5 minutes before</SelectItem>
                        <SelectItem value="10">10 minutes before</SelectItem>
                        <SelectItem value="15">15 minutes before</SelectItem>
                        <SelectItem value="30">30 minutes before</SelectItem>
                        <SelectItem value="60">1 hour before</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </div>

              <Separator />

              {/* Client Reminders Section */}
              <div>
                <h4 className="text-sm font-semibold mb-3">Client Reminders</h4>
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <div className="space-y-0.5">
                      <Label className="text-base">Reminder Call to Potential Client</Label>
                      <p className="text-sm text-muted-foreground">
                        AI calls the potential client before their appointment to confirm they're still available
                      </p>
                    </div>
                    <Switch
                      checked={bookingReminderConfig.clientConfirmationEnabled}
                      onCheckedChange={(checked) => setBookingReminderConfig(prev => ({ ...prev, clientConfirmationEnabled: checked }))}
                    />
                  </div>

                  <div className="grid gap-2">
                    <Label className={!bookingReminderConfig.clientConfirmationEnabled ? 'text-muted-foreground' : ''}>
                      Call client
                    </Label>
                    <Select
                      value={String(bookingReminderConfig.confirmationHoursBefore ?? 24)}
                      onValueChange={(v) => setBookingReminderConfig(prev => ({ ...prev, confirmationHoursBefore: parseFloat(v) }))}
                      disabled={!bookingReminderConfig.clientConfirmationEnabled}
                    >
                      <SelectTrigger className="w-56">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="0.25">15 minutes before</SelectItem>
                        <SelectItem value="1">1 hour before</SelectItem>
                        <SelectItem value="2">2 hours before</SelectItem>
                        <SelectItem value="4">4 hours before</SelectItem>
                        <SelectItem value="24">24 hours before</SelectItem>
                        <SelectItem value="48">48 hours before</SelectItem>
                      </SelectContent>
                    </Select>
                    <p className="text-xs text-muted-foreground">
                      Reminder calls only go out between 9am and 7pm in your timezone. A reminder that would land at 4am gets shifted forward to 9am the same day, so clients aren't called overnight.
                    </p>
                  </div>

                  <div className="grid gap-2">
                    <Label className={!bookingReminderConfig.clientConfirmationEnabled ? 'text-muted-foreground' : ''}>
                      Only for appointments booked
                    </Label>
                    <Select
                      value={String(bookingReminderConfig.confirmationMinDaysOut ?? 2)}
                      onValueChange={(v) => setBookingReminderConfig(prev => ({ ...prev, confirmationMinDaysOut: parseInt(v) }))}
                      disabled={!bookingReminderConfig.clientConfirmationEnabled}
                    >
                      <SelectTrigger className="w-56">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="0">Any time in advance</SelectItem>
                        <SelectItem value="1">1+ days out</SelectItem>
                        <SelectItem value="2">2+ days out</SelectItem>
                        <SelectItem value="3">3+ days out</SelectItem>
                        <SelectItem value="7">7+ days out</SelectItem>
                      </SelectContent>
                    </Select>
                    <p className="text-xs text-muted-foreground">
                      Same-day appointments won't get a reminder call
                    </p>
                  </div>
                </div>
              </div>

              {/* Client Emails Section — outbound calendar invites + reschedule
                  notifications to the prospect. Hidden for customer firms
                  because the From address is `noreply@legallady.ai`, which
                  is on-brand for LegalLady itself but would confuse a
                  Smith Law prospect ("why is a third party emailing me?").
                  Customer firms should rely on their connected calendar's
                  built-in invites. Only platform
                  super-admins (LegalLady's own org) see this toggle. */}
              {isSuperAdmin && (
                <>
                  <Separator />
                  <div>
                    <h4 className="text-sm font-semibold mb-1">Client Emails</h4>
                    <p className="text-xs text-muted-foreground mb-3">
                      Platform-only — sends from <code className="text-[11px]">noreply@legallady.ai</code>. Hidden for customer firms because the From domain wouldn't match their brand.
                    </p>
                    <div className="space-y-4">
                      <div className="flex items-center justify-between gap-4">
                        <div className="space-y-0.5">
                          <Label className="text-base">Email prospects calendar invites</Label>
                          <p className="text-sm text-muted-foreground">
                            When a booking is created or rescheduled, send the prospect an .ics calendar invite they can add to Apple/Google/Outlook. <span className="text-foreground font-medium">The recipient is the prospect's own email captured during their call/chat</span> — if no email was captured, no invite goes out (you'll see a toast explaining why).
                          </p>
                        </div>
                        <Switch
                          checked={bookingReminderConfig.bookingInvitesEnabled}
                          onCheckedChange={(checked) => setBookingReminderConfig(prev => ({ ...prev, bookingInvitesEnabled: checked }))}
                        />
                      </div>
                    </div>
                  </div>
                </>
              )}
              </div>

              {/* Bottom Save — mirrors the top one so the user always has a
                  Save button in reach when they finish editing a section,
                  rather than scrolling back up. Same handler, same disabled
                  state. */}
              <div className="mt-6 pt-4 border-t flex justify-end">
                <Button
                  onClick={handleSaveBookingReminderConfig}
                  disabled={savingBookingReminder}
                  className="bg-gradient-to-r from-brand-royal to-brand-periwinkle text-white hover:opacity-90 shadow-md shadow-brand-periwinkle/20 w-full sm:w-auto"
                >
                  {savingBookingReminder ? (
                    <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Saving…</>
                  ) : (
                    <><Save className="w-4 h-4 mr-2" /> Save booking settings</>
                  )}
                </Button>
              </div>
            </div>
          </div>
        </TabsContent>

        {/* Organization Tab (Admin only) */}
        {(agent?.role === 'admin' || isImpersonating) && (
          <TabsContent value="organization" className="space-y-5">
            <div className="rounded-xl border bg-card shadow-sm card-inset overflow-hidden">
              <div className="p-5 sm:p-6">
                <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3 mb-5">
                  <div>
                    <div className="flex items-center gap-2 mb-1">
                      <div className="h-7 w-7 rounded-lg bg-brand-royal/15 flex items-center justify-center">
                        <Building2 className="h-3.5 w-3.5 text-brand-royal" />
                      </div>
                      <h3 className="text-base font-semibold">Organization</h3>
                    </div>
                    <p className="text-sm text-muted-foreground ml-9">Identity and subscription tier for your firm.</p>
                  </div>
                  <Button
                    onClick={handleSaveOrgSettings}
                    disabled={savingOrg || (orgData.name === orgOriginal.name && orgData.slug === orgOriginal.slug) || !orgData.name.trim() || !orgData.slug.trim()}
                    className="bg-gradient-to-r from-brand-royal to-brand-periwinkle text-white hover:opacity-90 shadow-md shadow-brand-periwinkle/20 shrink-0 w-full sm:w-auto disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {savingOrg ? (
                      <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Saving…</>
                    ) : (
                      <><Save className="w-4 h-4 mr-2" /> Save changes</>
                    )}
                  </Button>
                </div>

                {loadingOrg ? (
                  <div className="flex items-center gap-2 text-sm text-muted-foreground py-6">
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Loading organization settings…
                  </div>
                ) : (
                  <div className="grid gap-4 sm:grid-cols-2">
                    <div className="grid gap-1.5">
                      <Label htmlFor="org-name" className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                        <Building2 className="h-3 w-3" /> Organization name
                      </Label>
                      <Input
                        id="org-name"
                        value={orgData.name}
                        onChange={(e) => setOrgData(prev => ({ ...prev, name: e.target.value }))}
                        placeholder="Your firm's name"
                      />
                    </div>

                    <div className="grid gap-1.5">
                      <Label htmlFor="org-slug" className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                        <Globe2 className="h-3 w-3" /> URL slug
                      </Label>
                      <Input
                        id="org-slug"
                        value={orgData.slug}
                        onChange={(e) => setOrgData(prev => ({ ...prev, slug: e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '') }))}
                        placeholder="your-org-slug"
                      />
                      <p className="text-xs text-muted-foreground">Lowercase letters, numbers, hyphens only.</p>
                    </div>

                    <div className="grid gap-1.5 sm:col-span-2">
                      <Label className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                        <Sparkles className="h-3 w-3" /> Subscription
                      </Label>
                      <div className="flex items-center gap-3 p-3 rounded-lg border border-border bg-muted/30">
                        <div className="h-9 w-9 rounded-lg bg-gradient-to-br from-brand-royal to-brand-periwinkle flex items-center justify-center shadow-sm">
                          <Sparkles className="h-4 w-4 text-white" />
                        </div>
                        <div className="flex-1">
                          <p className="text-sm font-semibold capitalize">{orgData.subscriptionTier || 'trial'}</p>
                          <p className="text-xs text-muted-foreground">Manage from Billing</p>
                        </div>
                        <Link to="/billing" className="text-xs font-semibold text-brand-periwinkle hover:underline shrink-0">
                          Open billing →
                        </Link>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </TabsContent>
        )}

        {/* Voice Tab (Admin only) - Guided Customization */}
        {(agent?.role === 'admin' || isImpersonating) && (
          <TabsContent value="voice">
            {voiceLocked ? (
              <LockedSettingsPanel
                feature="ai_inbound_agent"
                tier="partner"
                title="Voice settings are a Partner feature"
                description="Your AI voice receptionist activates on the Partner plan. Voice selection, greeting tone, business hours, and callback routing all live here once you upgrade."
              />
            ) : (
            <div className="space-y-6">
              {/* Phone Numbers — summary + link to Agents page for management */}
              <Card className="border bg-card shadow-sm card-inset overflow-hidden">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2 text-base">
                    <span className="h-7 w-7 rounded-lg bg-brand-periwinkle/15 flex items-center justify-center">
                      <Phone className="h-3.5 w-3.5 text-brand-periwinkle" />
                    </span>
                    Phone numbers
                  </CardTitle>
                  <CardDescription>
                    {loadingVoice
                      ? 'Loading…'
                      : phoneNumbers.length === 0
                        ? 'No phone numbers configured yet'
                        : `${phoneNumbers.filter(p => p.status === 'active').length} active · ${phoneNumbers.length} total`}
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                    <p className="text-sm text-muted-foreground">
                      Assign inbound/outbound AI agents, set schedules, and manage numbers on the Agents page.
                    </p>
                    <Button variant="outline" asChild className="shrink-0">
                      <Link to="/agents">
                        Manage on Agents page
                        <ExternalLink className="w-3 h-3 ml-1.5" />
                      </Link>
                    </Button>
                  </div>
                </CardContent>
              </Card>

              {/* Voice provider credentials managed internally — no user-facing config needed */}

              {/* AI Voice */}
              <Card className="border bg-card shadow-sm card-inset overflow-hidden">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2 text-base">
                    <span className="h-7 w-7 rounded-lg bg-brand-royal/15 flex items-center justify-center">
                      <Volume2 className="h-3.5 w-3.5 text-brand-royal" />
                    </span>
                    AI voice
                  </CardTitle>
                  <CardDescription>Your AI receptionist's voice</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="flex items-center gap-3 p-4 rounded-lg border border-primary/30 bg-primary/5">
                    <div className="flex-1">
                      <h4 className="font-semibold text-sm">Molly · {brandBaseName}'s AI receptionist</h4>
                      <p className="text-xs text-muted-foreground">Warm, professional, and natural — our default brand voice</p>
                    </div>
                    <Button
                      variant="ghost" size="sm" className="text-xs"
                      onClick={() => handlePreviewVoice('molly')}
                    >
                      <Volume2 className="h-3 w-3 mr-1" />
                      {playingVoice === 'molly' ? 'Stop' : 'Preview'}
                    </Button>
                  </div>
                </CardContent>
              </Card>

              {/* Voicemail preview — hear what callers reach when an outbound hits voicemail */}
              <Card className="border bg-card shadow-sm card-inset overflow-hidden">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2 text-base">
                    <span className="h-7 w-7 rounded-lg bg-violet-500/15 flex items-center justify-center">
                      <PhoneCall className="h-3.5 w-3.5 text-violet-600" />
                    </span>
                    Voicemail preview
                  </CardTitle>
                  <CardDescription>
                    What outbound callees hear when our AI hits their voicemail. Plays in your firm's selected voice.
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-3">
                  <div className="rounded-lg border bg-muted/30 p-3">
                    <p className="text-sm italic">
                      "{buildVoicemailMessage(voiceSettings.assistantName, orgData.name)}"
                    </p>
                  </div>
                  <div className="flex items-center justify-between">
                    <p className="text-[11px] text-muted-foreground">
                      Voicemail wording is templated from your agent name and firm name. To customize, edit them in the wizard.
                    </p>
                    <Button
                      variant="outline" size="sm"
                      onClick={handlePreviewVoicemail}
                      className="shrink-0"
                    >
                      <Volume2 className="h-3 w-3 mr-1.5" />
                      {previewingVoicemail ? 'Stop' : 'Listen'}
                    </Button>
                  </div>
                </CardContent>
              </Card>

              {/* Legal Lady calendar */}
              <Card className="border bg-card shadow-sm card-inset overflow-hidden">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2 text-base">
                    <span className="h-7 w-7 rounded-lg bg-emerald-500/15 flex items-center justify-center">
                      <Calendar className="h-3.5 w-3.5 text-emerald-600" />
                    </span>
                    Legal Lady calendar
                  </CardTitle>
                  <CardDescription>
                    Default availability rules used when no Nylas calendar is connected, and as booking guardrails for AI agents.
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="grid gap-4 md:grid-cols-2">
                    <div className="space-y-2">
                      <Label htmlFor="calendar-timezone" className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                        Calendar timezone
                      </Label>
                      <Select
                        value={voiceConfig.firmTimezone}
                        onValueChange={(value) => setVoiceConfig(prev => ({ ...prev, firmTimezone: value }))}
                      >
                        <SelectTrigger id="calendar-timezone">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {US_TIMEZONES.map(tz => (
                            <SelectItem key={tz.value} value={tz.value}>{tz.label}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="calendar-duration" className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                        Default appointment length
                      </Label>
                      <Select
                        value={String(voiceConfig.bookingConfig.consultationLengthMinutes)}
                        onValueChange={(value) => updateBookingConfig({ consultationLengthMinutes: Number(value) })}
                      >
                        <SelectTrigger id="calendar-duration">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {[15, 30, 45, 60, 90].map(minutes => (
                            <SelectItem key={minutes} value={String(minutes)}>{minutes} minutes</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="calendar-hours" className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                      Booking hours
                    </Label>
                    <Input
                      id="calendar-hours"
                      value={voiceConfig.businessHours}
                      onChange={(e) => setVoiceConfig(prev => ({ ...prev, businessHours: e.target.value }))}
                      placeholder="Monday through Friday, 9am to 5pm"
                    />
                    <p className="text-[11px] text-muted-foreground">
                      Use a plain phrase like "Monday through Friday, 9am to 5pm". The AI uses this in prompts, and Legal Lady calendar checks block times outside it.
                    </p>
                  </div>

                  <div className="flex items-center justify-between gap-3 p-3 rounded-lg border border-border bg-muted/20">
                    <div className="flex-1 min-w-0">
                      <Label className="text-sm font-medium">Only book inside business hours</Label>
                      <p className="text-xs text-muted-foreground mt-0.5">
                        Prevents the Legal Lady calendar tool from accepting after-hours or weekend slots.
                      </p>
                    </div>
                    <Switch
                      checked={voiceConfig.bookingConfig.businessHoursOnly !== false}
                      onCheckedChange={(checked) => updateBookingConfig({ businessHoursOnly: checked })}
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="calendar-blackouts" className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                      Blackout dates
                    </Label>
                    <Textarea
                      id="calendar-blackouts"
                      value={(voiceConfig.bookingConfig.blackoutDates || []).join('\n')}
                      onChange={(e) => updateBookingConfig({
                        blackoutDates: e.target.value
                          .split(/\n|,/)
                          .map(d => d.trim())
                          .filter(Boolean),
                      })}
                      placeholder={'2026-05-25\n2026-07-04'}
                      rows={3}
                    />
                    <p className="text-[11px] text-muted-foreground">
                      One date per line in YYYY-MM-DD format. The AI will not book appointments on these dates.
                    </p>
                  </div>

                  <div className="flex justify-end pt-1">
                    <Button
                      onClick={handleSaveCalendarConfig}
                      disabled={savingCalendarConfig}
                      className="bg-gradient-to-r from-brand-royal to-brand-periwinkle text-white hover:opacity-90"
                    >
                      {savingCalendarConfig ? (
                        <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Saving…</>
                      ) : (
                        <><Save className="w-4 h-4 mr-2" /> Save calendar</>
                      )}
                    </Button>
                  </div>
                </CardContent>
              </Card>

              {/* Recording disclosure — TCPA / two-party-consent compliance */}
              <Card className="border bg-card shadow-sm card-inset overflow-hidden">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2 text-base">
                    <span className="h-7 w-7 rounded-lg bg-amber-500/15 flex items-center justify-center">
                      <Shield className="h-3.5 w-3.5 text-amber-600" />
                    </span>
                    Recording disclosure
                  </CardTitle>
                  <CardDescription>
                    Required in two-party-consent states (CA, FL, IL, MD, MA, MT, NV, NH, PA, WA, CT) for outbound recorded calls.
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="flex items-center justify-between gap-3 p-3 rounded-lg border border-border bg-muted/20">
                    <div className="flex-1 min-w-0">
                      <Label className="text-sm font-medium">Enable disclosure</Label>
                      <p className="text-xs text-muted-foreground mt-0.5">
                        Inserts the disclosure message at call start. Agents rebuild automatically on save.
                      </p>
                    </div>
                    <Switch
                      checked={disclosureConfig.enabled}
                      onCheckedChange={(checked) => setDisclosureConfig(prev => ({ ...prev, enabled: checked }))}
                    />
                  </div>

                  <div className="grid gap-1.5">
                    <Label htmlFor="disclosure-message" className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                      Disclosure message
                    </Label>
                    <Textarea
                      id="disclosure-message"
                      value={disclosureConfig.message}
                      onChange={(e) => setDisclosureConfig(prev => ({ ...prev, message: e.target.value }))}
                      placeholder="Just a quick heads up — this call may be recorded for quality."
                      rows={2}
                      maxLength={300}
                      disabled={!disclosureConfig.enabled}
                      className={!disclosureConfig.enabled ? 'opacity-60' : ''}
                    />
                    <p className="text-[10px] text-muted-foreground/70">
                      Keep it short and warm — under 12 seconds spoken. Max 300 characters.
                    </p>
                  </div>

                  <div className="grid gap-2 sm:grid-cols-2">
                    <div className="flex items-start justify-between gap-3 p-3 rounded-lg border border-border bg-muted/20">
                      <div className="flex-1 min-w-0">
                        <Label className={`text-sm font-medium ${!disclosureConfig.enabled ? 'text-muted-foreground' : ''}`}>Apply on outbound</Label>
                        <p className="text-[11px] text-muted-foreground mt-0.5">
                          Agent says it as the first sentence after the lead picks up.
                        </p>
                      </div>
                      <Switch
                        checked={disclosureConfig.applyToOutbound}
                        disabled={!disclosureConfig.enabled}
                        onCheckedChange={(checked) => setDisclosureConfig(prev => ({ ...prev, applyToOutbound: checked }))}
                      />
                    </div>
                    <div className="flex items-start justify-between gap-3 p-3 rounded-lg border border-border bg-muted/20">
                      <div className="flex-1 min-w-0">
                        <Label className={`text-sm font-medium ${!disclosureConfig.enabled ? 'text-muted-foreground' : ''}`}>Apply on inbound</Label>
                        <p className="text-[11px] text-muted-foreground mt-0.5">
                          Prepends to the greeting callers hear when they reach your number.
                        </p>
                      </div>
                      <Switch
                        checked={disclosureConfig.applyToInbound}
                        disabled={!disclosureConfig.enabled}
                        onCheckedChange={(checked) => setDisclosureConfig(prev => ({ ...prev, applyToInbound: checked }))}
                      />
                    </div>
                  </div>

                  <div className="flex justify-end pt-1">
                    <Button
                      onClick={handleSaveDisclosureConfig}
                      disabled={savingDisclosure}
                      className="bg-gradient-to-r from-brand-royal to-brand-periwinkle text-white hover:opacity-90"
                    >
                      {savingDisclosure ? (
                        <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Saving…</>
                      ) : (
                        <><Save className="w-4 h-4 mr-2" /> Save disclosure</>
                      )}
                    </Button>
                  </div>
                </CardContent>
              </Card>

              {/* SMS guidance card removed — the underlying send_message tool
                  is a stub; we don't actually send SMS yet. Re-enable once
                  the SMS plumbing (Telnyx messaging profile + outbound send)
                  is wired into the call-handling code. */}

              {/* Do-not-call list — TCPA opt-out compliance */}
              <Card className="border bg-card shadow-sm card-inset overflow-hidden">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2 text-base">
                    <span className="h-7 w-7 rounded-lg bg-rose-500/15 flex items-center justify-center">
                      <Shield className="h-3.5 w-3.5 text-rose-600" />
                    </span>
                    Do-not-call list
                    <Badge variant="secondary" className="ml-1">{dncTotal}</Badge>
                  </CardTitle>
                  <CardDescription>
                    Outbound calls to numbers on this list are blocked before dialing. STOP, UNSUBSCRIBE, and CANCEL replies via SMS auto-add the sender.
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="grid gap-2 sm:grid-cols-[1fr_1fr_auto] items-end">
                    <div className="grid gap-1.5">
                      <Label htmlFor="dnc-phone" className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                        Phone number
                      </Label>
                      <Input
                        id="dnc-phone"
                        value={dncNewNumber}
                        onChange={(e) => setDncNewNumber(e.target.value)}
                        placeholder="+15551234567"
                      />
                    </div>
                    <div className="grid gap-1.5">
                      <Label htmlFor="dnc-notes" className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                        Notes (optional)
                      </Label>
                      <Input
                        id="dnc-notes"
                        value={dncNewNotes}
                        onChange={(e) => setDncNewNotes(e.target.value)}
                        placeholder="Why are we adding this number?"
                      />
                    </div>
                    <Button
                      onClick={handleAddDncEntry}
                      disabled={dncAdding || !dncNewNumber.trim()}
                      className="bg-gradient-to-r from-brand-royal to-brand-periwinkle text-white hover:opacity-90 shrink-0"
                    >
                      {dncAdding ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Add'}
                    </Button>
                  </div>

                  <div className="flex items-center gap-2">
                    <Input
                      value={dncSearch}
                      onChange={(e) => setDncSearch(e.target.value)}
                      placeholder="Search by phone number…"
                      className="max-w-xs"
                      onKeyDown={(e) => { if (e.key === 'Enter') loadDncEntries(dncSearch.trim() || undefined) }}
                    />
                    <Button
                      variant="outline" size="sm"
                      onClick={() => loadDncEntries(dncSearch.trim() || undefined)}
                      disabled={dncLoading}
                    >
                      Search
                    </Button>
                    {dncSearch && (
                      <Button
                        variant="ghost" size="sm"
                        onClick={() => { setDncSearch(''); loadDncEntries() }}
                      >
                        Clear
                      </Button>
                    )}
                  </div>

                  <div className="rounded-lg border border-border overflow-hidden">
                    {dncLoading ? (
                      <div className="flex items-center gap-2 text-sm text-muted-foreground p-4">
                        <Loader2 className="w-4 h-4 animate-spin" /> Loading…
                      </div>
                    ) : dncEntries.length === 0 ? (
                      <div className="text-sm text-muted-foreground p-6 text-center">
                        No numbers on the do-not-call list.
                      </div>
                    ) : (
                      <div className="divide-y divide-border">
                        {dncEntries.map(entry => (
                          <div key={entry.id} className="flex items-center gap-3 p-3 text-sm">
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2">
                                <span className="font-mono">{formatPhoneDisplay(entry.phoneNumber)}</span>
                                <Badge
                                  variant="outline"
                                  className={
                                    entry.source === 'reply' ? 'border-amber-500/30 text-amber-700' :
                                    entry.source === 'callee_request' ? 'border-blue-500/30 text-blue-700' :
                                    'border-border text-muted-foreground'
                                  }
                                >
                                  {entry.source === 'reply' ? 'STOP reply' : entry.source === 'callee_request' ? 'Callee asked' : 'Manual'}
                                </Badge>
                              </div>
                              {entry.notes && (
                                <p className="text-xs text-muted-foreground mt-0.5 truncate">{entry.notes}</p>
                              )}
                              <p className="text-[10px] text-muted-foreground/60 mt-0.5">
                                Added {new Date(entry.createdAt).toLocaleDateString()}
                              </p>
                            </div>
                            <Button
                              variant="ghost" size="sm" className="text-rose-600 hover:text-rose-700 hover:bg-rose-50 dark:hover:bg-rose-950"
                              onClick={() => handleRemoveDncEntry(entry.id, entry.phoneNumber)}
                            >
                              <Trash2 className="w-3.5 h-3.5" />
                            </Button>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>

              {/* Empty state for firms with no agents yet */}
              {assistants.length === 0 && !loadingVoice && (
                <div className="relative overflow-hidden rounded-xl border border-amber-500/30 bg-gradient-to-br from-amber-500/5 via-card to-card shadow-sm card-inset">
                  <div className="absolute -top-16 -right-16 h-48 w-48 rounded-full bg-amber-500/10 blur-3xl" aria-hidden="true" />
                  <div className="relative p-8 text-center">
                    <div className="mx-auto h-16 w-16 rounded-2xl bg-gradient-to-br from-amber-500/20 to-amber-500/5 border border-amber-500/30 flex items-center justify-center mb-4">
                      <Bot className="h-7 w-7 text-amber-600 dark:text-amber-400" />
                    </div>
                    <h3 className="font-semibold text-lg mb-1">No AI agents yet</h3>
                    <p className="text-sm text-muted-foreground mb-5 max-w-sm mx-auto">
                      Create your AI agents to configure voice, greetings, and schedules.
                    </p>
                    <Button asChild className="bg-gradient-to-r from-brand-royal to-brand-periwinkle text-white hover:opacity-90 shadow-md shadow-brand-periwinkle/20">
                      <Link to="/agents">
                        <Bot className="h-4 w-4 mr-2" />
                        Go to AI Agents
                      </Link>
                    </Button>
                  </div>
                </div>
              )}

              {/* Live Call Activity (this firm) */}
              <Card className="border bg-card shadow-sm card-inset overflow-hidden">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2 text-base">
                    <span className="h-7 w-7 rounded-lg bg-violet-500/15 flex items-center justify-center">
                      <Users2 className="h-3.5 w-3.5 text-violet-500" />
                    </span>
                    Live Call Activity
                  </CardTitle>
                  <CardDescription>Concurrent calls happening right now at your firm.</CardDescription>
                </CardHeader>
                <CardContent className="space-y-5">
                  {/* Live usage bar (this org only) */}
                  <div className="space-y-2">
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-muted-foreground">Your Active Calls</span>
                      <span className="font-medium">
                        {concurrencyStatus.activeCount} active
                        {concurrencyStatus.queueDepth > 0 && ` · ${concurrencyStatus.queueDepth} waiting`}
                      </span>
                    </div>
                    <div className="h-3 w-full rounded-full bg-muted overflow-hidden">
                      <div
                        className={`h-full rounded-full transition-colors duration-500 ${
                          concurrencyStatus.activeCount === 0 ? 'bg-muted' : 'bg-green-500'
                        }`}
                        style={{ width: `${Math.min((concurrencyStatus.activeCount / 10) * 100, 100)}%` }}
                      />
                    </div>
                    <p className="text-xs text-muted-foreground">
                      {concurrencyStatus.activeCount === 0
                        ? 'No active calls right now.'
                        : `${concurrencyStatus.activeCount} call${concurrencyStatus.activeCount !== 1 ? 's' : ''} in progress.`}
                    </p>
                  </div>

                  {/* Overflow message field removed — it was saved to settings
                      but the call router never read it, so editing it had no
                      effect on what overflow callers actually heard. Restore
                      once concurrencyConfig.overflowMessage is read by the
                      Telnyx queue/overflow handler. */}
                </CardContent>
              </Card>

              {/* Follow-up for leads — Principal feature. Hidden on lower tiers
                  so users don't tweak settings the API will reject. The
                  feature itself (CallQueueService retries) is fully wired. */}
              {featureGate.isAllowed('auto_callback') && (
              <Card className="border bg-card shadow-sm card-inset overflow-hidden">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2 text-base">
                    <span className="h-7 w-7 rounded-lg bg-amber-500/15 flex items-center justify-center">
                      <PhoneCall className="h-3.5 w-3.5 text-amber-600 dark:text-amber-400" />
                    </span>
                    Follow-up for leads
                  </CardTitle>
                  <CardDescription>
                    Automatically follow up on missed calls, voicemails, incomplete intakes, and unanswered outbound lead calls on a schedule you control.
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-6">
                  {/* Enable/disable toggle */}
                  <div className="flex items-center justify-between">
                    <div className="space-y-0.5">
                      <Label>Auto-Follow-Up Enabled</Label>
                      <p className="text-xs text-muted-foreground">
                        Place outbound follow-up calls only after your firm explicitly turns this on
                      </p>
                    </div>
                    <Switch
                      checked={callbackConfig.enabled}
                      onCheckedChange={(checked) => setCallbackConfig(prev => ({ ...prev, enabled: checked }))}
                    />
                  </div>

                  <Separator />

                  {/* Auto-cancel toggle */}
                  <div className="flex items-center justify-between">
                    <div className="space-y-0.5">
                      <Label className={!callbackConfig.enabled ? 'text-muted-foreground' : ''}>Auto-Cancel When Caller Calls Back</Label>
                      <p className="text-xs text-muted-foreground">
                        Cancel pending callbacks when the caller calls your number again
                      </p>
                    </div>
                    <Switch
                      checked={callbackConfig.autoCancelOnCallback}
                      onCheckedChange={(checked) => setCallbackConfig(prev => ({ ...prev, autoCancelOnCallback: checked }))}
                      disabled={!callbackConfig.enabled}
                    />
                  </div>

                  <Separator />

                  {/* Max retries */}
                  <div className="grid gap-2">
                    <Label htmlFor="maxRetries" className={!callbackConfig.enabled ? 'text-muted-foreground' : ''}>Maximum Retry Attempts</Label>
                    <Input
                      id="maxRetries"
                      type="number"
                      min={1}
                      max={10}
                      value={callbackConfig.maxRetries}
                      onChange={(e) => setCallbackConfig(prev => ({ ...prev, maxRetries: Math.max(1, Math.min(10, parseInt(e.target.value) || 1)) }))}
                      disabled={!callbackConfig.enabled}
                      className="w-24"
                    />
                    <p className="text-xs text-muted-foreground">
                      Number of times to retry if the callback goes unanswered (1-10)
                    </p>
                  </div>

                  {/* Callback window */}
                  <div className="grid gap-2">
                    <Label htmlFor="expiryHours" className={!callbackConfig.enabled ? 'text-muted-foreground' : ''}>Callback Window</Label>
                    <Select
                      value={String(callbackConfig.expiryHours)}
                      onValueChange={(v) => setCallbackConfig(prev => ({ ...prev, expiryHours: parseFloat(v) }))}
                      disabled={!callbackConfig.enabled}
                    >
                      <SelectTrigger className="w-48">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="1">1 hour</SelectItem>
                        <SelectItem value="2">2 hours</SelectItem>
                        <SelectItem value="4">4 hours</SelectItem>
                        <SelectItem value="8">8 hours</SelectItem>
                        <SelectItem value="12">12 hours</SelectItem>
                        <SelectItem value="24">24 hours</SelectItem>
                        <SelectItem value="48">48 hours</SelectItem>
                      </SelectContent>
                    </Select>
                    <p className="text-xs text-muted-foreground">
                      How long to keep retrying before giving up
                    </p>
                  </div>

                  {/* Delay before first callback */}
                  <div className="grid gap-2">
                    <Label htmlFor="delayMinutes" className={!callbackConfig.enabled ? 'text-muted-foreground' : ''}>Delay Before First Callback</Label>
                    <Select
                      value={String(callbackConfig.delayMinutes)}
                      onValueChange={(v) => setCallbackConfig(prev => ({ ...prev, delayMinutes: parseInt(v) }))}
                      disabled={!callbackConfig.enabled}
                    >
                      <SelectTrigger className="w-48">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="5">5 minutes</SelectItem>
                        <SelectItem value="10">10 minutes</SelectItem>
                        <SelectItem value="15">15 minutes</SelectItem>
                        <SelectItem value="30">30 minutes</SelectItem>
                      </SelectContent>
                    </Select>
                    <p className="text-xs text-muted-foreground">
                      Wait time before the first follow-up attempt after a missed call, voicemail, incomplete intake, or unanswered outbound lead call
                    </p>
                  </div>

                  <Separator />

                  {/* Business hours only */}
                  <div className="flex items-center justify-between">
                    <div className="space-y-0.5">
                      <Label className={!callbackConfig.enabled ? 'text-muted-foreground' : ''}>Only During Business Hours</Label>
                      <p className="text-xs text-muted-foreground">
                        Follow-up calls will only be placed during your configured business hours
                      </p>
                    </div>
                    <Switch
                      checked={callbackConfig.businessHoursOnly}
                      onCheckedChange={(checked) => setCallbackConfig(prev => ({ ...prev, businessHoursOnly: checked }))}
                      disabled={!callbackConfig.enabled}
                    />
                  </div>

                  <Separator />

                  {/* Per-recipient cooldown */}
                  <div className="grid gap-2">
                    <Label htmlFor="recipientCooldown">Per-recipient cooldown</Label>
                    <Select
                      value={String(callbackConfig.recipientCooldownMinutes)}
                      onValueChange={(v) => setCallbackConfig(prev => ({ ...prev, recipientCooldownMinutes: parseInt(v) }))}
                    >
                      <SelectTrigger className="w-48">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="0">Off (no cooldown)</SelectItem>
                        <SelectItem value="5">5 minutes</SelectItem>
                        <SelectItem value="10">10 minutes</SelectItem>
                        <SelectItem value="15">15 minutes</SelectItem>
                        <SelectItem value="30">30 minutes</SelectItem>
                        <SelectItem value="60">1 hour</SelectItem>
                        <SelectItem value="240">4 hours</SelectItem>
                      </SelectContent>
                    </Select>
                    <p className="text-xs text-muted-foreground">
                      Refuse a fresh outbound call to the same person within this window — stops accidental dial-bomb scenarios from form re-submissions, retry races, or manual triggers. Applies regardless of whether auto-follow-up is on. We recommend 10 minutes.
                    </p>
                  </div>

                  <Separator />

                  {/* Retry cadence */}
                  <div className="grid gap-2">
                    <Label className={!callbackConfig.enabled ? 'text-muted-foreground' : ''}>Retry Cadence</Label>
                    <Select
                      value={
                        JSON.stringify(callbackConfig.retryCadence) === JSON.stringify([5, 30, 120, 480])
                          ? 'aggressive'
                          : JSON.stringify(callbackConfig.retryCadence) === JSON.stringify([15, 60, 480, 1440])
                          ? 'standard'
                          : JSON.stringify(callbackConfig.retryCadence) === JSON.stringify([60, 480, 1440, 2880])
                          ? 'patient'
                          : 'custom'
                      }
                      onValueChange={(v) => {
                        const presets: Record<string, number[]> = {
                          aggressive: [5, 30, 120, 480],
                          standard: [15, 60, 480, 1440],
                          patient: [60, 480, 1440, 2880],
                        }
                        if (presets[v]) {
                          setCallbackConfig(prev => ({ ...prev, retryCadence: presets[v] }))
                        }
                      }}
                      disabled={!callbackConfig.enabled}
                    >
                      <SelectTrigger className="w-72">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="aggressive">Aggressive (5min &rarr; 30min &rarr; 2hr &rarr; 8hr)</SelectItem>
                        <SelectItem value="standard">Standard (15min &rarr; 1hr &rarr; 8hr &rarr; 24hr)</SelectItem>
                        <SelectItem value="patient">Patient (1hr &rarr; 8hr &rarr; 24hr &rarr; 48hr)</SelectItem>
                        <SelectItem value="custom" disabled>Custom</SelectItem>
                      </SelectContent>
                    </Select>
                    {/* Visual cadence timeline */}
                    <div className="flex flex-wrap items-center gap-1 text-xs text-muted-foreground mt-1">
                      <span className="font-medium">Attempt 1:</span>
                      <span>{callbackConfig.delayMinutes}min</span>
                      {callbackConfig.retryCadence.map((minutes, idx) => {
                        const label = minutes < 60
                          ? `${minutes}min`
                          : minutes < 1440
                          ? `${minutes / 60}hr`
                          : `${minutes / 1440}d`
                        return (
                          <React.Fragment key={idx}>
                            <span className="text-muted-foreground/50">&rarr;</span>
                            <span className="font-medium">#{idx + 2}:</span>
                            <span>{label}</span>
                          </React.Fragment>
                        )
                      })}
                    </div>
                    <p className="text-xs text-muted-foreground">
                      Time between each retry attempt after the first callback
                    </p>
                  </div>

                  {/* Save button */}
                  <div className="flex justify-end">
                    <Button
                      onClick={handleSaveCallbackConfig}
                      disabled={savingCallback}
                      size="sm"
                    >
                      <Save className="w-4 h-4 mr-2" />
                      {savingCallback ? 'Saving...' : 'Save Follow-Up Settings'}
                    </Button>
                  </div>
                </CardContent>
              </Card>
              )}



              {/* Save Button — sticky on mobile so it's always reachable */}
              {assistants.length > 0 && (
                <div className="flex justify-end sticky bottom-4 z-10">
                  <Button
                    onClick={handleSaveVoiceSettings}
                    disabled={savingVoice}
                    className="bg-gradient-to-r from-brand-royal to-brand-periwinkle text-white hover:opacity-90 shadow-lg shadow-brand-periwinkle/30 backdrop-blur-sm"
                  >
                    {savingVoice ? (
                      <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Saving…</>
                    ) : (
                      <><Save className="w-4 h-4 mr-2" /> Save voice settings</>
                    )}
                  </Button>
                </div>
              )}
            </div>
            )}

            {/* Agent wizard removed — now lives on AI Agents page */}
          </TabsContent>
        )}

        {/* Widget Tab (Admin only) */}
        {(agent?.role === 'admin' || isImpersonating) && (
          <TabsContent value="widget" className="space-y-5">
            <button
              onClick={() => navigate('/widget')}
              className="block w-full text-left rounded-xl border bg-card shadow-sm card-inset overflow-hidden hover:border-brand-periwinkle/40 hover:bg-brand-periwinkle/[0.02] transition-colors group"
            >
              <div className="p-5 sm:p-6 flex items-center gap-4">
                <div className="h-14 w-14 rounded-2xl bg-gradient-to-br from-brand-royal/15 to-brand-periwinkle/15 flex items-center justify-center shrink-0">
                  <Code className="h-6 w-6 text-brand-royal" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-base font-semibold">Chat Widget &amp; embed</p>
                  <p className="text-sm text-muted-foreground mt-0.5">Branding, colors, conversation starters, animations, and the embed code.</p>
                </div>
                <ChevronRight className="h-5 w-5 text-muted-foreground group-hover:text-brand-periwinkle transition-colors shrink-0" />
              </div>
            </button>
          </TabsContent>
        )}

        {/* Lead Follow-Up Tab (Admin only) */}
        {(agent?.role === 'admin' || isImpersonating) && (
          <TabsContent value="lead-followup">
            {followUpLocked ? (
              <LockedSettingsPanel
                feature="ai_outbound_agent"
                tier="principal"
                title="Lead Follow-Up is a Principal feature"
                description="Connect your website's contact form so the AI calls every inquiry back personally within moments — turning form submissions into live conversations while the lead is still warm. Available on the Principal plan."
              />
            ) : (
            <div className="space-y-6">
              <Card className="border bg-card shadow-sm card-inset overflow-hidden">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2 text-base">
                    <span className="h-7 w-7 rounded-lg bg-amber-500/15 flex items-center justify-center">
                      <Zap className="h-3.5 w-3.5 text-amber-600 dark:text-amber-400" />
                    </span>
                    Lead follow-up triggers
                  </CardTitle>
                  <CardDescription>
                    Automatically trigger AI outbound calls when leads submit a form on your website
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="rounded-xl border border-amber-500/30 bg-gradient-to-br from-amber-500/5 to-transparent p-4">
                    <p className="text-xs font-bold uppercase tracking-wider text-amber-700 dark:text-amber-400 mb-3">How it works</p>
                    <ol className="space-y-2.5">
                      {[
                        `Connect your website contact form to ${brandBaseName}`,
                        'A lead submits the form and consents to a call — your AI outbound agent is triggered',
                        'The AI calls within minutes to qualify and book a consultation',
                      ].map((step, i) => (
                        <li key={i} className="flex items-start gap-3">
                          <span className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-amber-500/15 text-[10px] font-bold text-amber-700 dark:text-amber-400 mt-0.5">{i + 1}</span>
                          <span className="text-sm text-foreground/80 leading-relaxed">{step}</span>
                        </li>
                      ))}
                    </ol>
                  </div>
                  <Link
                    to="/form-connections"
                    className="block rounded-xl border bg-card hover:border-amber-500/40 hover:bg-amber-500/[0.03] transition-colors group"
                  >
                    <div className="p-4 flex items-center gap-3">
                      <div className="h-10 w-10 rounded-lg bg-amber-500/15 flex items-center justify-center shrink-0">
                        <ExternalLink className="h-4 w-4 text-amber-600 dark:text-amber-400" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-semibold">Manage form connections</p>
                        <p className="text-xs text-muted-foreground mt-0.5">Webhooks, Zapier, n8n, and custom endpoints.</p>
                      </div>
                      <ChevronRight className="h-4 w-4 text-muted-foreground group-hover:text-amber-500 transition-colors shrink-0" />
                    </div>
                  </Link>
                </CardContent>
              </Card>
            </div>
            )}
          </TabsContent>
        )}
      </Tabs>
    </div>
  )
}
